<script>
  import { createEventDispatcher, onMount } from "svelte";
  import { slide } from "svelte/transition";
  import { TextInput, TextArea, Button } from "carbon-components-svelte";
  import ChevronRight from "carbon-icons-svelte/lib/ChevronRight.svelte";
  import ChevronDown from "carbon-icons-svelte/lib/ChevronDown.svelte";
  import ChevronUp from "carbon-icons-svelte/lib/ChevronUp.svelte";
  import Copy from "carbon-icons-svelte/lib/Copy.svelte";
  import Printer from "carbon-icons-svelte/lib/Printer.svelte";
  import TrashCan from "carbon-icons-svelte/lib/TrashCan.svelte";
  import Checkmark from "carbon-icons-svelte/lib/Checkmark.svelte";
  import DocumentAdd from "carbon-icons-svelte/lib/DocumentAdd.svelte";
  import Save from "carbon-icons-svelte/lib/Save.svelte";
  import WarningFilled from "carbon-icons-svelte/lib/WarningFilled.svelte";
  import DeliveryTruck from "carbon-icons-svelte/lib/DeliveryTruck.svelte";
  import Edit from "carbon-icons-svelte/lib/Edit.svelte";
  import Search from "carbon-icons-svelte/lib/Search.svelte";
  import Close from "carbon-icons-svelte/lib/Close.svelte";
  import User from "carbon-icons-svelte/lib/User.svelte";
  import Enterprise from "carbon-icons-svelte/lib/Enterprise.svelte";
  import Locked from "carbon-icons-svelte/lib/Locked.svelte";
  import Document from "carbon-icons-svelte/lib/Document.svelte";

  import { customerQuote, loadAddressText, loadAddresCoordinate, yards, quoteSelectedMaterial, routeTravelEstimates, activeTab, selectedQuoteForJob } from "../../stores";
  import { reportYardImageError, inlineAddress, portal, isFullAddress, validateFullAddress } from "../../helpers";
  import { saveQuote, fetchCompanies, saveCompany, fetchYards } from "../../data.js";
  import { calculateFleetDaysAndCosts, getFullAndRemainderDays } from "./fleetScheduleCalculator.js";
  import { generateQuotePdf } from "./quotePdfGenerator.js";
  import PhoneInput from "../../components/PhoneInput.svelte";
  import { formatPhoneNumber, toE164 } from "../../phoneUtils.js";
  import CompanyForm from "../../forms/Company/CompanyForm.svelte";

  const dispatch = createEventDispatcher();

  export let isPage = false;
  let showCustomerDetails = true;
  let copyFeedback = false;
  let copyFeedbackTimeout = null;

  let pdfFeedback = false;
  let pdfFeedbackTimeout = null;

  let isSavingQuote = false;
  let saveFeedbackMessage = "";
  let saveFeedbackType = ""; // "success" | "error"
  let saveFeedbackTimeout = null;

  // Company management state
  let companies = [];
  let isLoadingCompanies = false;
  let companySearchTerm = "";
  let isCompanySearchFocused = false;
  let selectedCompany = null;
  let showEditCompanyModal = false;

  // Initialize selectedCompany from customerQuote store if already present
  if ($customerQuote?.selectedCompany) {
    selectedCompany = $customerQuote.selectedCompany;
  } else if ($customerQuote?.customerName && $customerQuote.customerName.trim()) {
    selectedCompany = {
      name: $customerQuote.customerName.trim(),
      phone: $customerQuote.companyPhone || $customerQuote.phone || "",
      address: $customerQuote.companyAddress || null,
      contacts: []
    };
  }

  $: isCompanySelected = Boolean(
    (selectedCompany && (selectedCompany.name?.trim() || selectedCompany.id)) ||
    ($customerQuote?.selectedCompany && ($customerQuote.selectedCompany.name?.trim() || $customerQuote.selectedCompany.id)) ||
    ($customerQuote?.customerName && $customerQuote.customerName.trim().length > 0 && selectedCompany)
  );

  // Prepared For (contact person) state
  let preparedForInput = "";
  let isPreparedForFocused = false;
  let showSaveContactModal = false;
  let newContactData = {
    name: "",
    position: "",
    phone: "",
    email: ""
  };
  let blurTimeout = null;

  onMount(() => {
    loadCompaniesList();
    if (!$yards || $yards.length === 0) {
      fetchYards().catch(err => console.warn("[CustomerQuote] Failed to fetch yards:", err));
    }
  });

  async function loadCompaniesList() {
    try {
      isLoadingCompanies = true;
      const res = await fetchCompanies();
      companies = Array.isArray(res) ? res : (res?.companies || []);
      if (!selectedCompany) {
        if ($customerQuote.selectedCompany) {
          selectedCompany = $customerQuote.selectedCompany;
        } else if ($customerQuote.customerName) {
          matchCompanyByName($customerQuote.customerName);
        }
      }
    } catch (err) {
      console.error("[CustomerQuote] Failed to fetch companies:", err);
    } finally {
      isLoadingCompanies = false;
    }
  }

  function matchCompanyByName(name) {
    if (!name) return;
    const match = companies.find(c => c.name && c.name.trim().toLowerCase() === name.trim().toLowerCase());
    if (match) {
      selectedCompany = match;
      if (!selectedCompany.contacts) selectedCompany.contacts = match.people || [];
      $customerQuote.selectedCompany = match;
    } else {
      selectedCompany = {
        name: name,
        phone: $customerQuote.companyPhone || $customerQuote.phone || "",
        address: $customerQuote.companyAddress || null,
        contacts: []
      };
    }
  }

  $: if ($customerQuote.customerName && !selectedCompany && companies.length > 0) {
    matchCompanyByName($customerQuote.customerName);
  }

  $: if ($customerQuote.selectedCompany && (!selectedCompany || selectedCompany.id !== $customerQuote.selectedCompany.id)) {
    selectedCompany = $customerQuote.selectedCompany;
  }

  // Filter companies based on search input
  $: filteredCompanies = (companies || []).filter(c => {
    if (!companySearchTerm.trim()) return false;
    const term = companySearchTerm.toLowerCase();
    const nameMatch = c.name && c.name.toLowerCase().includes(term);
    const abnMatch = c.ABN && String(c.ABN).toLowerCase().includes(term);
    const phoneMatch = c.phone && String(c.phone).toLowerCase().includes(term);
    const suburbMatch = c.address?.suburb && c.address.suburb.toLowerCase().includes(term);
    return nameMatch || abnMatch || phoneMatch || suburbMatch;
  });

  function selectCompany(comp) {
    selectedCompany = comp;
    if (!selectedCompany.contacts) {
      selectedCompany.contacts = comp.people || [];
    }
    $customerQuote.customerName = comp.name || "";
    $customerQuote.selectedCompany = comp;
    $customerQuote.companyAddress = comp.address || null;
    $customerQuote.companyPhone = comp.phone || "";
    companySearchTerm = "";
    isCompanySearchFocused = false;

    // Check if quote has a contactPerson already; if not and company has contacts, default to main
    if (!$customerQuote.contactPerson && Array.isArray(selectedCompany.contacts) && selectedCompany.contacts.length > 0) {
      const mainContact = selectedCompany.contacts.find(c => c.main) || selectedCompany.contacts[0];
      if (mainContact) {
        selectContact(mainContact);
      }
    }
  }

  function clearSelectedCompany() {
    selectedCompany = null;
    companySearchTerm = "";
    isCompanySearchFocused = false;
    preparedForInput = "";
    $customerQuote.customerName = "";
    $customerQuote.contactPerson = "";
    $customerQuote.prepared_for = "";
    $customerQuote.selectedCompany = null;
    $customerQuote.companyAddress = null;
    $customerQuote.companyPhone = "";
    $customerQuote.phone = "";
    $customerQuote.email = "";
    $customerQuote.position = "";
    isPreparedForFocused = false;
  }

  function handleCreateNewCompany(name) {
    selectedCompany = {
      name: name,
      phone: "",
      address: {},
      ABN: "",
      ACN: "",
      contacts: []
    };
    $customerQuote.customerName = name;
    $customerQuote.selectedCompany = selectedCompany;
    companySearchTerm = "";
    isCompanySearchFocused = false;
    showEditCompanyModal = true;
  }

  function handleCompanySaved(e) {
    if (e.detail) {
      selectedCompany = e.detail;
      if (!selectedCompany.contacts) selectedCompany.contacts = selectedCompany.people || [];
      $customerQuote.customerName = selectedCompany.name;
      $customerQuote.selectedCompany = selectedCompany;
      $customerQuote.companyAddress = selectedCompany.address;
      $customerQuote.companyPhone = selectedCompany.phone;

      // Update in companies cache
      const idx = companies.findIndex(c => (c.id != null && c.id === selectedCompany.id) || (c.name && c.name === selectedCompany.name));
      if (idx !== -1) {
        companies[idx] = selectedCompany;
      } else {
        companies = [...companies, selectedCompany];
      }
    }
    showEditCompanyModal = false;
  }

  // Prepared For contact person synchronization & handling
  $: if ($customerQuote.contactPerson && $customerQuote.contactPerson !== preparedForInput && !isPreparedForFocused) {
    preparedForInput = $customerQuote.contactPerson;
  }

  $: companyContacts = (selectedCompany?.contacts && Array.isArray(selectedCompany.contacts))
    ? selectedCompany.contacts
    : ((selectedCompany?.people && Array.isArray(selectedCompany.people)) ? selectedCompany.people : []);

  $: filteredContacts = companyContacts.filter(c => {
    if (!preparedForInput.trim()) return true;
    const q = preparedForInput.toLowerCase();
    const nameMatch = c.name && c.name.toLowerCase().includes(q);
    const posMatch = c.position && c.position.toLowerCase().includes(q);
    const emailMatch = c.email && c.email.toLowerCase().includes(q);
    return nameMatch || posMatch || emailMatch;
  });

  $: exactContactMatch = companyContacts.some(c =>
    c.name && c.name.trim().toLowerCase() === preparedForInput.trim().toLowerCase()
  );

  function selectContact(contact) {
    if (blurTimeout) {
      clearTimeout(blurTimeout);
      blurTimeout = null;
    }
    preparedForInput = contact.name || "";
    $customerQuote.contactPerson = contact.name || "";
    $customerQuote.prepared_for = contact.name || "";
    $customerQuote.phone = contact.phone || "";
    $customerQuote.email = contact.email || "";
    $customerQuote.position = contact.position || "";
    isPreparedForFocused = false;
  }

  let lastPromptedContactName = "";

  function promptSaveContactDetails(name) {
    if (blurTimeout) {
      clearTimeout(blurTimeout);
      blurTimeout = null;
    }
    isPreparedForFocused = false;
    const targetName = name || preparedForInput.trim();
    if (!targetName) return;
    $customerQuote.contactPerson = targetName;
    $customerQuote.prepared_for = targetName;
    $customerQuote.phone = "";
    $customerQuote.email = "";
    $customerQuote.position = "";
    newContactData = {
      name: targetName,
      position: "",
      phone: "",
      email: ""
    };
    showSaveContactModal = true;
  }

  function handlePreparedForKeyDown(e) {
    if (e.key === "Tab") {
      if (blurTimeout) {
        clearTimeout(blurTimeout);
        blurTimeout = null;
      }
      isPreparedForFocused = false;
      const trimmed = preparedForInput.trim();
      if (!trimmed) {
        $customerQuote.contactPerson = "";
        $customerQuote.prepared_for = "";
        $customerQuote.phone = "";
        $customerQuote.email = "";
        $customerQuote.position = "";
        return;
      }
      const match = companyContacts.find(c => c.name && c.name.trim().toLowerCase() === trimmed.toLowerCase());
      if (match) {
        selectContact(match);
      } else {
        $customerQuote.contactPerson = trimmed;
        $customerQuote.prepared_for = trimmed;
        $customerQuote.phone = "";
        $customerQuote.email = "";
        $customerQuote.position = "";
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (blurTimeout) {
        clearTimeout(blurTimeout);
        blurTimeout = null;
      }
      isPreparedForFocused = false;
      const trimmed = preparedForInput.trim();
      if (!trimmed) return;
      const match = companyContacts.find(c => c.name && c.name.trim().toLowerCase() === trimmed.toLowerCase());
      if (match) {
        selectContact(match);
      } else {
        $customerQuote.contactPerson = trimmed;
        $customerQuote.prepared_for = trimmed;
        $customerQuote.phone = "";
        $customerQuote.email = "";
        $customerQuote.position = "";
        promptSaveContactDetails(trimmed);
      }
    } else if (e.key === "Escape") {
      isPreparedForFocused = false;
    }
  }

  function handlePreparedForBlur() {
    blurTimeout = setTimeout(() => {
      isPreparedForFocused = false;
      const trimmed = preparedForInput.trim();
      if (!trimmed) {
        $customerQuote.contactPerson = "";
        $customerQuote.prepared_for = "";
        $customerQuote.phone = "";
        $customerQuote.email = "";
        $customerQuote.position = "";
        return;
      }

      // Check if matches an existing contact in company contacts
      const match = companyContacts.find(c => c.name && c.name.trim().toLowerCase() === trimmed.toLowerCase());
      if (match) {
        selectContact(match);
      } else {
        // Person not on list:
        // Set quote contact to typed name, hide dropdown button and clear phone & email
        $customerQuote.contactPerson = trimmed;
        $customerQuote.prepared_for = trimmed;
        $customerQuote.phone = "";
        $customerQuote.email = "";
        $customerQuote.position = "";
      }
    }, 120);
  }

  async function handleSaveNewContact() {
    if (!newContactData.name || !newContactData.name.trim()) return;

    const contactToSave = {
      name: newContactData.name.trim(),
      position: newContactData.position?.trim() || "",
      phone: newContactData.phone ? toE164(newContactData.phone.trim()) : "",
      email: newContactData.email?.trim() || "",
      main: !selectedCompany || !selectedCompany.contacts || selectedCompany.contacts.length === 0
    };

    if (selectedCompany) {
      if (!Array.isArray(selectedCompany.contacts)) {
        selectedCompany.contacts = [];
      }
      selectedCompany.contacts = [...selectedCompany.contacts, contactToSave];
      selectedCompany.people = selectedCompany.contacts;
      try {
        const saved = await saveCompany(selectedCompany);
        if (saved) {
          selectedCompany = saved;
          if (!selectedCompany.contacts) selectedCompany.contacts = selectedCompany.people || [];
          selectedCompany.people = selectedCompany.contacts;
          const idx = companies.findIndex(c => (c.id != null && c.id === saved.id) || (c.name && c.name === saved.name));
          if (idx !== -1) {
            companies[idx] = selectedCompany;
          } else {
            companies = [...companies, selectedCompany];
          }
        }
      } catch (err) {
        console.error("[handleSaveNewContact] Failed to save contact to DB:", err);
      }
    }

    // Set newly added person as selected on quote
    selectContact(contactToSave);
    showSaveContactModal = false;
    isPreparedForFocused = false;
  }

  function handleUseContactWithoutSaving() {
    const trimmed = (newContactData.name || preparedForInput || "").trim();
    $customerQuote.contactPerson = trimmed;
    $customerQuote.prepared_for = trimmed;
    $customerQuote.phone = newContactData.phone || "";
    $customerQuote.email = newContactData.email || "";
    $customerQuote.position = newContactData.position || "";
    preparedForInput = trimmed;
    showSaveContactModal = false;
    isPreparedForFocused = false;
  }

  // Helper to look up disposal site address from package or associated Yard
  function resolvePkgDisposalAddress(p) {
    if (!p) return "";
    if (typeof p.disposalAddress === "string" && p.disposalAddress.trim()) {
      return p.disposalAddress.trim();
    }
    if (typeof p.disposal_address === "string" && p.disposal_address.trim()) {
      return p.disposal_address.trim();
    }
    if (p.disposal_address && typeof p.disposal_address === "object") {
      const inline = inlineAddress(p.disposal_address);
      if (inline) return inline;
      if (p.disposal_address.place_name) return p.disposal_address.place_name;
      if (p.disposal_address.street) return p.disposal_address.street;
    }
    if (typeof p.dispose_address === "string" && p.dispose_address.trim()) {
      return p.dispose_address.trim();
    }
    if (p.dispose_address && typeof p.dispose_address === "object") {
      const inline = inlineAddress(p.dispose_address);
      if (inline) return inline;
      if (p.dispose_address.place_name) return p.dispose_address.place_name;
      if (p.dispose_address.street) return p.dispose_address.street;
    }
    const matchedYard = ($yards || []).find(y =>
      (p.yardId != null && String(y.id) === String(p.yardId)) ||
      (p.yard && (String(y.id) === String(p.yard.id) || y.name === p.yard.name)) ||
      (p.yardName && (y.name === p.yardName || y.yard_name === p.yardName))
    ) || p.yard;

    if (matchedYard) {
      if (typeof matchedYard.address === "string" && matchedYard.address.trim()) {
        return matchedYard.address.trim();
      }
      if (matchedYard.address && typeof matchedYard.address === "object") {
        const inline = inlineAddress(matchedYard.address);
        if (inline) return inline;
        if (matchedYard.address.street) return matchedYard.address.street;
        if (matchedYard.address.place_name) return matchedYard.address.place_name;
      }
      if (matchedYard.suburb) {
        return matchedYard.name ? `${matchedYard.name}, ${matchedYard.suburb}` : matchedYard.suburb;
      }
    }

    if (p.suburb) {
      return p.yardName ? `${p.yardName}, ${p.suburb}` : p.suburb;
    }

    return "";
  }

  // Auto-populate active package's site address if not set yet
  $: if ($loadAddressText && activePkg && !activePkg.siteAddress) {
    activePkg.siteAddress = $loadAddressText;
    activePkg.load_address = (typeof activePkg.load_address === "object" && activePkg.load_address !== null)
      ? { ...activePkg.load_address, street: $loadAddressText }
      : { street: $loadAddressText };
  }

  // Multi-material packages resolution (pure derived, no store mutations in reactive block)
  $: packages = ($customerQuote.haulagePackages && $customerQuote.haulagePackages.length > 0)
    ? $customerQuote.haulagePackages
    : ($customerQuote.haulagePackage ? [$customerQuote.haulagePackage] : []);
  $: hasHaulage = packages.length > 0;

  // Active package resolution by unique id (with fallback to activeMaterialTab or first available package)
  $: activePkg = (packages.find(p => p.id === $customerQuote.activePackageId) ||
                 packages.find(p => (p.material || 'Material 1') === $customerQuote.activeMaterialTab) ||
                 packages[0] ||
                 null);
  $: activeTabId = activePkg ? activePkg.id : null;
  $: activeTabName = activePkg ? (activePkg.material || "Material 1") : "General Material";
  $: pkg = activePkg;

  // Auto-populate disposal address from Yard address for active package and all packages
  $: if (activePkg && $yards) {
    if (!activePkg.disposalAddress) {
      const resolvedDisp = resolvePkgDisposalAddress(activePkg);
      if (resolvedDisp) {
        activePkg.disposalAddress = resolvedDisp;
        if (!activePkg.disposal_address) {
          activePkg.disposal_address = { street: resolvedDisp };
        }
        if (!activePkg.dispose_address) {
          activePkg.dispose_address = resolvedDisp;
        }
      }
    }
  }

  $: if (packages && $yards) {
    packages.forEach(p => {
      if (!p.disposalAddress) {
        const resolvedDisp = resolvePkgDisposalAddress(p);
        if (resolvedDisp) {
          p.disposalAddress = resolvedDisp;
          if (!p.disposal_address) {
            p.disposal_address = { street: resolvedDisp };
          }
          if (!p.dispose_address) {
            p.dispose_address = resolvedDisp;
          }
        }
      }
    });
  }

  $: allocatedTrucks = activePkg ? Math.max(1, parseInt(activePkg.allocatedTrucks, 10) || 1) : 1;

  $: baseTonneRate = hasHaulage && pkg ? Number(pkg.tonneRate || 0) : 0;
  $: baseCostPerLoad = hasHaulage && pkg ? Number(pkg.costPerLoad || 0) : 0;
  $: baseDayRate = hasHaulage && pkg ? Number(pkg.dayRate || 0) : 0;
  $: truckCapacity = hasHaulage && pkg ? Number(pkg.totalCapacity || 1) : 1;
  $: dailyRuns = hasHaulage && pkg ? Number(pkg.runs || 0) : 0;
  $: tonnes = hasHaulage && pkg
    ? Math.max(0, Number(String(pkg.totalTonnes || pkg.tonnesDeliveredInDay || (truckCapacity * dailyRuns) || 0).replace(/,/g, '')) || 0)
    : 0;

  // Turnaround & Schedule calculations for active package
  // Physical constraint: If totalTonnes and truckCapacity are specified (> 0),
  // totalLoads MUST be Math.ceil(tonnes / truckCapacity). Stale/cached estimates
  // must never override the actual physical trips required to move the scope.
  $: totalLoads = hasHaulage && pkg
    ? (tonnes > 0 && truckCapacity > 0
        ? Math.ceil(tonnes / truckCapacity)
        : (Number(pkg.schedule?.total_loads ?? pkg.totalLoads) || Number(dailyRuns || 1)))
    : 0;

  $: totalDays1Truck = (dailyRuns > 0 && totalLoads > 0)
    ? (totalLoads / dailyRuns)
    : (tonnes > 0 && truckCapacity > 0 && dailyRuns > 0
        ? (Math.ceil(tonnes / truckCapacity) / dailyRuns)
        : Number(pkg?.totalDays || 0));

  // Discrete-event fleet schedule calculation considering loading site queue and time-of-day traffic routes
  $: activePkgFleet = (hasHaulage && pkg)
    ? calculateFleetDaysAndCosts({
        allocatedTrucks,
        totalLoads,
        totalTonnes: tonnes,
        truckCapacity,
        dayRate: baseDayRate,
        startTime: pkg.startTime || "07:00",
        finishTime: pkg.finishTime || "17:00",
        loadTime: pkg.loadTime != null ? pkg.loadTime : 10,
        tipTime: pkg.tipTime != null ? pkg.tipTime : 10,
        routes: pkg.load?.routes || null,
        mapDurationMinutes: pkg.travelMins || ($routeTravelEstimates && pkg.yardId ? $routeTravelEstimates[pkg.yardId]?.durationMinutes : null),
        isEstimate: pkg.isEstimate ?? (!pkg.load || !pkg.load.routes || pkg.load.routes.length === 0),
        singleTruckDays: totalDays1Truck,
        dailyRuns1Truck: dailyRuns,
      })
    : null;

  $: calculatedDays = activePkgFleet
    ? activePkgFleet.calculatedDays
    : ((dailyRuns > 0 && totalLoads > 0 && allocatedTrucks > 0)
        ? (totalLoads / (dailyRuns * allocatedTrucks))
        : (totalDays1Truck > 0 ? (totalDays1Truck / allocatedTrucks) : 0));

  $: daysSplit = getFullAndRemainderDays(calculatedDays);
  $: fullDays = daysSplit.fullDays;
  $: remainderDays = daysSplit.remainderDays;
  $: billedDays = daysSplit.billedDays;

  // Helper to extract base tolls for a package based on load/loads count
  function getPackageBaseTolls(p, lds) {
    if (!p) return 0;
    if (p.tollPerLoad != null && p.tollPerLoad > 0) {
      const loadsCount = lds != null ? lds : (p.totalLoads || p.runs || 1);
      return Number((p.tollPerLoad * loadsCount).toFixed(2));
    }
    if (p.totalTolls != null) return Number(p.totalTolls);
    const load = p.load;
    if (load) {
      const dailyTolls = load.total_tolls != null
        ? Number(load.total_tolls)
        : (load.routes || []).reduce((acc, r) => acc + (r.toll || 0), 0) / 100;
      const runs = load.total_loads || p.runs || 1;
      const tPerLoad = runs > 0 ? (dailyTolls / runs) : 0;
      const loadsCount = lds != null ? lds : (p.totalLoads || runs);
      return Number((tPerLoad * loadsCount).toFixed(2));
    }
    return 0;
  }

  // Base Cartage Cost = Allocated Trucks x Full days x Truck Rate + Truck Rate x Remainder days
  $: baseCartageCost = activePkgFleet
    ? activePkgFleet.baseCartageCost
    : (hasHaulage && pkg ? (allocatedTrucks * fullDays * baseDayRate) + (baseDayRate * remainderDays) : 0);

  $: isPerLoadDisposal = (pkg?.disposal_fee_type === "per_load");
  $: loadsPerTrip = Number(pkg?.disposal_loads_per_trip || 1);
  $: billableLoads = isPerLoadDisposal ? ((totalLoads || 0) * loadsPerTrip) : (totalLoads || 0);
  $: baseTolls = hasHaulage && pkg ? getPackageBaseTolls(pkg, totalLoads) : 0;
  $: baseMaterialCost = hasHaulage && pkg
    ? (isPerLoadDisposal
        ? (billableLoads * baseCostPerLoad)
        : (tonnes * baseTonneRate))
    : 0;
  $: baseTotalCost = hasHaulage && pkg ? (baseCartageCost + baseMaterialCost + baseTolls) : 0;

  function incrementTrucks() {
    if (!activePkg) return;
    const current = Math.max(1, parseInt(activePkg.allocatedTrucks, 10) || 1);
    activePkg.allocatedTrucks = current + 1;
    $customerQuote.allocatedTrucks = current + 1;
    $customerQuote = $customerQuote;
  }

  function decrementTrucks() {
    if (!activePkg) return;
    const current = Math.max(1, parseInt(activePkg.allocatedTrucks, 10) || 1);
    if (current > 1) {
      activePkg.allocatedTrucks = current - 1;
      $customerQuote.allocatedTrucks = current - 1;
      $customerQuote = $customerQuote;
    }
  }

  function handleTrucksInput(e) {
    if (!activePkg) return;
    let val = parseInt(e.target.value, 10);
    if (isNaN(val) || val < 1) {
      val = 1;
    }
    activePkg.allocatedTrucks = val;
    $customerQuote.allocatedTrucks = val;
    $customerQuote = $customerQuote;
  }

  function handleTonnesInput(e) {
    if (!pkg) return;
    const rawVal = e.target.value;
    const val = parseFloat(String(rawVal).replace(/,/g, ''));
    const safeVal = isNaN(val) || val < 0 ? 0 : val;
    pkg.totalTonnes = safeVal;
    const cap = truckCapacity || 38;
    const newLoads = (safeVal > 0 && cap > 0) ? Math.ceil(safeVal / cap) : (dailyRuns || 1);
    pkg.totalLoads = newLoads;
    const runs = Number(dailyRuns || pkg.runs || 1);
    pkg.totalDays = (runs > 0 && newLoads > 0) ? (newLoads / runs) : 1;
    if (pkg.schedule) {
      pkg.schedule.total_loads = newLoads;
      pkg.schedule.days_single_truck = pkg.totalDays;
    }
    if ($customerQuote.haulagePackage && ($customerQuote.haulagePackage.id === pkg.id || $customerQuote.haulagePackage.material === pkg.material)) {
      $customerQuote.haulagePackage.totalTonnes = safeVal;
      $customerQuote.haulagePackage.totalLoads = newLoads;
      $customerQuote.haulagePackage.totalDays = pkg.totalDays;
    }
    if (Array.isArray($customerQuote.haulagePackages)) {
      const match = $customerQuote.haulagePackages.find(p => p.id === pkg.id || p.material === pkg.material);
      if (match) {
        match.totalTonnes = safeVal;
        match.totalLoads = newLoads;
        match.totalDays = pkg.totalDays;
      }
    }
    $customerQuote = $customerQuote;
  }


  function formatDays(days) {
    if (days === null || days === undefined || isNaN(days) || days <= 0) return "0 days";
    const num = Number(days);
    const formatted = num % 1 === 0 ? num.toString() : num.toFixed(1);
    return `${formatted} ${num === 1 ? 'day' : 'days'}`;
  }

  function applyMargin(base, margin) {
    if (!base || isNaN(base)) return 0;
    if (!margin || margin.value === null || margin.value === "" || isNaN(margin.value)) return base;
    const val = Number(margin.value);
    if (margin.mode === "percent") {
      return base * (1 + val / 100);
    } else {
      return base + val;
    }
  }

  $: currentMargins = activePkg?.margins || $customerQuote.margins;

  function toggleMarginMode(fieldKey) {
    const target = activePkg?.margins || $customerQuote.margins;
    if (!target || !target[fieldKey]) return;
    const currentMode = target[fieldKey].mode;
    target[fieldKey].mode = currentMode === "percent" ? "dollar" : "percent";
    $customerQuote = $customerQuote;
  }

  function applyPresetMargin(percent) {
    const target = activePkg?.margins || $customerQuote.margins;
    if (target) {
      target.tonneRate = { value: 0, mode: "percent" };
      target.costPerLoad = { value: 0, mode: "percent" };
      target.dayRate = { value: 0, mode: "percent" };
      target.cartageCost = { value: 0, mode: "percent" };
      target.totalTolls = { value: 0, mode: "percent" };
      target.totalCost = { value: percent, mode: "percent" };
    }
    $customerQuote.marginPercent = percent;
    $customerQuote = $customerQuote;
  }

  function resetMargins() {
    const target = activePkg?.margins || $customerQuote.margins;
    if (target) {
      target.tonneRate = { value: 0, mode: "percent" };
      target.costPerLoad = { value: 0, mode: "percent" };
      target.dayRate = { value: 0, mode: "percent" };
      target.cartageCost = { value: 0, mode: "percent" };
      target.totalTolls = { value: 0, mode: "percent" };
      target.totalCost = { value: 0, mode: "percent" };
    }
    $customerQuote = $customerQuote;
  }

  $: {
    if (currentMargins && !currentMargins.totalTolls) {
      currentMargins.totalTolls = { value: 0, mode: "percent" };
    }
  }

  // Margin ratios & individual adjustments for active package
  $: mTR = currentMargins?.tonneRate;
  $: mCPL = currentMargins?.costPerLoad;
  $: mDR = currentMargins?.dayRate;
  $: mCC = currentMargins?.cartageCost;
  $: mTL = currentMargins?.totalTolls;
  $: mTC = currentMargins?.totalCost;

  $: hasIndivMargin = !!(
    (mTR && Number(mTR.value) !== 0) ||
    (mCPL && Number(mCPL.value) !== 0) ||
    (mDR && Number(mDR.value) !== 0) ||
    (mCC && Number(mCC.value) !== 0) ||
    (mTL && Number(mTL.value) !== 0)
  );

  // Overall total margin ratio (if applied)
  $: totalMarginRatio = (mTC && mTC.mode === "percent")
    ? (Number(mTC.value || 0) / 100)
    : (baseTotalCost > 0 ? (Number(mTC?.value || 0) / baseTotalCost) : 0);

  // Adjusted rates:
  $: adjustedTonneRate = (mTR && Number(mTR.value) !== 0)
    ? applyMargin(baseTonneRate, mTR)
    : (baseTonneRate * (1 + (hasIndivMargin ? 0 : totalMarginRatio)));

  $: adjustedCostPerLoad = (mCPL && Number(mCPL.value) !== 0)
    ? applyMargin(baseCostPerLoad, mCPL)
    : ((mTR && Number(mTR.value) !== 0)
        ? (adjustedTonneRate * truckCapacity)
        : (baseCostPerLoad * (1 + (hasIndivMargin ? 0 : totalMarginRatio))));

  $: adjustedDayRate = (mDR && Number(mDR.value) !== 0)
    ? applyMargin(baseDayRate, mDR)
    : (baseDayRate * (1 + (hasIndivMargin ? 0 : totalMarginRatio)));

  $: adjustedCartageCost = (mCC && Number(mCC.value) !== 0)
    ? applyMargin(baseCartageCost, mCC)
    : ((mDR && Number(mDR.value) !== 0)
        ? ((allocatedTrucks * fullDays * adjustedDayRate) + ((fullDays === 0 ? allocatedTrucks : 1) * adjustedDayRate * remainderDays))
        : (baseCartageCost * (1 + (hasIndivMargin ? 0 : totalMarginRatio))));

  $: adjustedTolls = (mTL && Number(mTL.value) !== 0)
    ? applyMargin(baseTolls, mTL)
    : (baseTolls * (1 + (hasIndivMargin ? 0 : totalMarginRatio)));

  // Subtotal (ex GST)
  $: intermediateSubtotal = hasIndivMargin
    ? ((mTR && Number(mTR.value) !== 0
        ? (adjustedTonneRate * tonnes)
        : (mCPL && Number(mCPL.value) !== 0
            ? (adjustedCostPerLoad * billableLoads)
            : baseMaterialCost)) + adjustedCartageCost + adjustedTolls)
    : baseTotalCost;

  $: subtotal = (mTC && Number(mTC.value) !== 0 && hasIndivMargin)
    ? applyMargin(intermediateSubtotal, mTC)
    : (hasIndivMargin ? intermediateSubtotal : applyMargin(baseTotalCost, mTC));

  $: adjustedTotalCost = subtotal;
  $: marginAmount = Math.max(0, subtotal - baseTotalCost);
  $: gst = subtotal * 0.1;
  $: totalIncGst = subtotal + gst;
  $: ratePerTonne = tonnes > 0 ? totalIncGst / tonnes : 0;

  // Financial calculation for ANY package (used for combined summary)
  function calculatePackageFinancials(p) {
    if (!p) return null;
    const bTR = Number(p.tonneRate || 0);
    const bCPL = Number(p.costPerLoad || 0);
    const bDR = Number(p.dayRate || 0);
    const cap = Number(p.totalCapacity || 1);
    const runs = Number(p.runs || 0);
    const t = Math.max(0, Number(String(p.totalTonnes || p.tonnesDeliveredInDay || (cap * runs) || 0).replace(/,/g, '')) || 0);
    const lds = (t > 0 && cap > 0)
      ? Math.ceil(t / cap)
      : (p.schedule?.total_loads != null && p.schedule.total_loads > 0
          ? Number(p.schedule.total_loads)
          : (p.totalLoads != null && p.totalLoads > 0 ? Number(p.totalLoads) : (runs || 0)));
    const allocTrucks = Math.max(1, parseInt(p.allocatedTrucks, 10) || 1);
    const pFleet = calculateFleetDaysAndCosts({
      allocatedTrucks: allocTrucks,
      totalLoads: lds,
      totalTonnes: t,
      truckCapacity: cap,
      dayRate: bDR,
      startTime: p.startTime || "07:00",
      finishTime: p.finishTime || "17:00",
      loadTime: p.loadTime != null ? p.loadTime : 10,
      tipTime: p.tipTime != null ? p.tipTime : 10,
      routes: p.load?.routes || null,
      mapDurationMinutes: p.travelMins || ($routeTravelEstimates && p.yardId ? $routeTravelEstimates[p.yardId]?.durationMinutes : null),
      isEstimate: p.isEstimate ?? (!p.load || !p.load.routes || p.load.routes.length === 0),
      singleTruckDays: Number(p.totalDays || 0),
      dailyRuns1Truck: runs,
    });
    const calcD = pFleet.calculatedDays;
    const split = getFullAndRemainderDays(calcD);
    const bCC = pFleet.baseCartageCost;
    const isPerLoad = (p.disposal_fee_type === "per_load");
    const pLoadsPerTrip = Number(p.disposal_loads_per_trip || 1);
    const pBillableLoads = isPerLoad ? (lds * pLoadsPerTrip) : lds;
    const bMat = isPerLoad ? (pBillableLoads * bCPL) : (t * bTR);
    const bTolls = getPackageBaseTolls(p, lds);
    const bTotal = bCC + bMat + bTolls;

    const pm = p.margins || {};
    const trM = pm.tonneRate;
    const cplM = pm.costPerLoad;
    const drM = pm.dayRate;
    const ccM = pm.cartageCost;
    const tlM = pm.totalTolls;
    const tcM = pm.totalCost;

    const hasIndiv = !!(
      (trM && Number(trM.value) !== 0) ||
      (cplM && Number(cplM.value) !== 0) ||
      (drM && Number(drM.value) !== 0) ||
      (ccM && Number(ccM.value) !== 0) ||
      (tlM && Number(tlM.value) !== 0)
    );

    const totalRatio = (tcM && tcM.mode === "percent")
      ? (Number(tcM.value || 0) / 100)
      : (bTotal > 0 ? (Number(tcM?.value || 0) / bTotal) : 0);

    const adjTR = (trM && Number(trM.value) !== 0)
      ? applyMargin(bTR, trM)
      : (bTR * (1 + (hasIndiv ? 0 : totalRatio)));

    const adjCPL = (cplM && Number(cplM.value) !== 0)
      ? applyMargin(bCPL, cplM)
      : ((trM && Number(trM.value) !== 0)
          ? (adjTR * cap)
          : (bCPL * (1 + (hasIndiv ? 0 : totalRatio))));

    const adjDR = (drM && Number(drM.value) !== 0)
      ? applyMargin(bDR, drM)
      : (bDR * (1 + (hasIndiv ? 0 : totalRatio)));

    const adjCC = (ccM && Number(ccM.value) !== 0)
      ? applyMargin(bCC, ccM)
      : ((drM && Number(drM.value) !== 0)
          ? ((allocTrucks * split.fullDays * adjDR) + ((split.fullDays === 0 ? allocTrucks : 1) * adjDR * split.remainderDays))
          : (bCC * (1 + (hasIndiv ? 0 : totalRatio))));

    const adjTolls = (tlM && Number(tlM.value) !== 0)
      ? applyMargin(bTolls, tlM)
      : (bTolls * (1 + (hasIndiv ? 0 : totalRatio)));

    const intermediate = hasIndiv
      ? ((trM && Number(trM.value) !== 0
          ? (adjTR * t)
          : (cplM && Number(cplM.value) !== 0
              ? (adjCPL * pBillableLoads)
              : bMat)) + adjCC + adjTolls)
      : bTotal;

    const sub = (tcM && Number(tcM.value) !== 0 && hasIndiv)
      ? applyMargin(intermediate, tcM)
      : (hasIndiv ? intermediate : applyMargin(bTotal, tcM));

    const g = sub * 0.1;
    const totInc = sub + g;
    const clientRate = t > 0 ? totInc / t : 0;

    return {
      id: p.id,
      material: p.material || "General Material",
      yardName: p.yardName,
      suburb: p.suburb,
      tonnes: t,
      loads: lds,
      allocatedTrucks: allocTrucks,
      calculatedDays: calcD,
      baseCartageCost: bCC,
      baseTolls: bTolls,
      quotedTolls: adjTolls,
      baseTotalCost: bTotal,
      subtotal: sub,
      gst: g,
      totalIncGst: totInc,
      clientRate,
      marginAmount: Math.max(0, sub - bTotal)
    };
  }

  $: allPackagesFinancials = packages.map(p => calculatePackageFinancials(p)).filter(Boolean);
  $: combinedBaseCost = allPackagesFinancials.reduce((sum, f) => sum + f.baseTotalCost, 0);
  $: combinedMarginAmount = allPackagesFinancials.reduce((sum, f) => sum + f.marginAmount, 0);
  $: combinedSubtotal = allPackagesFinancials.reduce((sum, f) => sum + f.subtotal, 0);
  $: combinedGst = combinedSubtotal * 0.1;
  $: combinedGrandTotal = combinedSubtotal + combinedGst;
  $: combinedTonnes = allPackagesFinancials.reduce((sum, f) => sum + f.tonnes, 0);
  $: combinedAverageRate = combinedTonnes > 0 ? combinedGrandTotal / combinedTonnes : 0;

  $: packageFinancialsMap = (allPackagesFinancials || []).reduce((acc, f) => {
    if (f && f.id) acc[f.id] = f;
    if (f && f.material) acc[f.material] = f;
    return acc;
  }, {});

  function getPkgFinancials(p) {
    if (!p) return null;
    return packageFinancialsMap[p.id] || packageFinancialsMap[p.material] || calculatePackageFinancials(p);
  }

  function switchTab(targetPkg) {
    if (!targetPkg) return;
    $customerQuote.activePackageId = targetPkg.id;
    $customerQuote.activeMaterialTab = targetPkg.material || "Material 1";
    $customerQuote.haulagePackage = targetPkg;
    $customerQuote = $customerQuote;
  }

  function removePackage(pkgId) {
    if (!$customerQuote.haulagePackages) return;
    const removeIdx = $customerQuote.haulagePackages.findIndex(p => p.id === pkgId);
    if (removeIdx !== -1) {
      $customerQuote.haulagePackages.splice(removeIdx, 1);
      $customerQuote.haulagePackages = [...$customerQuote.haulagePackages];
      if ($customerQuote.haulagePackages.length > 0) {
        const nextPkg = $customerQuote.haulagePackages[0];
        $customerQuote.activePackageId = nextPkg.id;
        $customerQuote.activeMaterialTab = nextPkg.material || "Material 1";
        $customerQuote.haulagePackage = nextPkg;
      } else {
        $customerQuote.activePackageId = null;
        $customerQuote.activeMaterialTab = "";
        $customerQuote.haulagePackage = null;
      }
      $customerQuote = $customerQuote;
    }
  }

  function switchMaterialTab(matName, idx) {
    const target = (packages && packages[idx]) || (packages && packages.find(p => p.material === matName)) || (packages && packages[0]);
    if (target) switchTab(target);
  }

  function removeMaterialPackage(matName, idx) {
    const target = (packages && packages[idx]) || (packages && packages.find(p => p.material === matName));
    if (target && target.id) removePackage(target.id);
  }

  function clearAllPackages() {
    $customerQuote.haulagePackages = [];
    $customerQuote.haulagePackage = null;
    $customerQuote.activePackageId = null;
    $customerQuote.activeMaterialTab = "";
    $customerQuote = $customerQuote;
  }

  function closeThisWindow() {
    dispatch("close-quote-window");
  }

  function clearHaulagePackage() {
    clearAllPackages();
  }

  function resetQuote() {
    if (confirm("Reset quote and generate a new quote reference?")) {
      $customerQuote = {
        id: null,
        quoteNumber: null,
        quoteDate: new Date().toISOString().split("T")[0],
        validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        customerName: "",
        contactPerson: "",
        prepared_for: "",
        phone: "",
        email: "",
        selectedCompany: null,
        companyAddress: null,
        companyPhone: "",
        notes: "Rates based on standard operating hours (07:00 - 17:00). Tolls and disposal fees included. Subject to clean tipping guidelines.",
        marginPercent: 15,
        margins: {
          tonneRate: { value: 0, mode: "percent" },
          costPerLoad: { value: 0, mode: "percent" },
          dayRate: { value: 0, mode: "percent" },
          cartageCost: { value: 0, mode: "percent" },
          totalTolls: { value: 0, mode: "percent" },
          totalCost: { value: 15, mode: "percent" }
        },
        allocatedTrucks: 1,
        haulagePackage: null,
        haulagePackages: [],
        activeMaterialTab: ""
      };
      selectedCompany = null;
      preparedForInput = "";
      companySearchTerm = "";
      saveFeedbackMessage = "";
    }
  }

  async function handleSaveQuote() {
    if (isSavingQuote) return;

    if (!isCompanySelected) {
      saveFeedbackType = "error";
      saveFeedbackMessage = "Company is a required field. Please select or add a company.";
      return;
    }

    if (!$customerQuote.customerName && selectedCompany?.name) {
      $customerQuote.customerName = selectedCompany.name;
    }

    if (preparedForInput && !$customerQuote.contactPerson) {
      $customerQuote.contactPerson = preparedForInput.trim();
    }
    $customerQuote.prepared_for = $customerQuote.contactPerson || preparedForInput || "";

    if ($customerQuote.phone && $customerQuote.phone.trim()) {
      $customerQuote.phone = toE164($customerQuote.phone.trim());
    }

    if (selectedCompany) {
      $customerQuote.selectedCompany = selectedCompany;
      $customerQuote.companyAddress = selectedCompany.address || null;
      $customerQuote.companyPhone = selectedCompany.phone || "";
    }

    // Ensure all haulage packages have load_address, polylines and coordinates resolved before saving
    if ($customerQuote.haulagePackages && Array.isArray($customerQuote.haulagePackages)) {
      $customerQuote.haulagePackages.forEach((p) => {
        if (!p.siteAddress && $loadAddressText) {
          p.siteAddress = $loadAddressText;
        }
        if (!p.load_address) {
          p.load_address = {
            street: p.siteAddress || $loadAddressText || "",
            coordinates: p.loadCoordinates || $loadAddresCoordinate || null
          };
        } else if (typeof p.load_address === "object") {
          if (!p.load_address.street && p.siteAddress) {
            p.load_address.street = p.siteAddress;
          }
          if (!p.load_address.coordinates && (p.loadCoordinates || $loadAddresCoordinate)) {
            p.load_address.coordinates = p.loadCoordinates || $loadAddresCoordinate;
          }
        }
        if (!p.polyline) {
          p.polyline = p.load?.polyline || p.geometry || null;
        }
        delete p.geometry;
        if (p.load) delete p.load.geometry;
        if (!p.loadCoordinates && $loadAddresCoordinate) {
          p.loadCoordinates = $loadAddresCoordinate;
        }
        if (!p.yard && $yards) {
          p.yard = $yards.find((y) => String(y.id) === String(p.yardId)) || null;
        }
        if (!p.disposalAddress) {
          p.disposalAddress = resolvePkgDisposalAddress(p);
        }
        if (!p.disposal_address && p.disposalAddress) {
          p.disposal_address = { street: p.disposalAddress };
        }
        if (!p.dispose_address && p.disposalAddress) {
          p.dispose_address = p.disposalAddress;
        }
      });
    }

    if ($customerQuote.haulagePackage) {
      const hp = $customerQuote.haulagePackage;
      if (!hp.siteAddress && $loadAddressText) hp.siteAddress = $loadAddressText;
      if (!hp.load_address) {
        hp.load_address = {
          street: hp.siteAddress || $loadAddressText || "",
          coordinates: hp.loadCoordinates || $loadAddresCoordinate || null
        };
      }
      if (!hp.disposalAddress) hp.disposalAddress = resolvePkgDisposalAddress(hp);
      if (!hp.disposal_address && hp.disposalAddress) hp.disposal_address = { street: hp.disposalAddress };
      if (!hp.dispose_address && hp.disposalAddress) hp.dispose_address = hp.disposalAddress;
    }

    isSavingQuote = true;
    saveFeedbackMessage = "";
    saveFeedbackType = "";

    try {
      console.log("[handleSaveQuote] Calling saveQuote with:", $customerQuote);
      const saved = await saveQuote($customerQuote);
      console.log("[handleSaveQuote] Saved quote received:", saved);
      if (saved && saved.id) {
        $customerQuote.id = saved.id;
      }
      if (saved && (saved.quote_number || saved.quoteNumber)) {
        $customerQuote.quoteNumber = saved.quote_number || saved.quoteNumber;
      }
      saveFeedbackType = "success";
      saveFeedbackMessage = `Quote successfully saved to database! (ID: #${$customerQuote.id || saved.id})`;
      dispatch("quote-saved", { quote: saved || $customerQuote });
      return saved;
    } catch (err) {
      console.error("[handleSaveQuote] Error saving quote:", err);
      saveFeedbackType = "error";
      saveFeedbackMessage = `Failed to save quote: ${err.message || err}`;
      return null;
    } finally {
      isSavingQuote = false;
      if (saveFeedbackTimeout) clearTimeout(saveFeedbackTimeout);
      saveFeedbackTimeout = setTimeout(() => {
        saveFeedbackMessage = "";
        saveFeedbackType = "";
      }, 5000);
    }
  }

  async function handleGoToQuote() {
    if (!isCompanySelected) {
      saveFeedbackMessage = "Please select a company before opening Quote Details.";
      saveFeedbackType = "error";
      return;
    }
    try {
      if (!$customerQuote.id) {
        saveFeedbackMessage = "Please save the quote before opening Quote Details.";
        saveFeedbackType = "error";
        return;
      }
      // Persist any current edits before navigating
      await handleSaveQuote();
      // Navigate to the Quote Details tab
      $activeTab = "quote-details";
    } catch (err) {
      console.error("[handleGoToQuote] Error:", err);
      saveFeedbackMessage = `Failed to open Quote Details: ${err.message || err}`;
      saveFeedbackType = "error";
    }
  }

  function formatCurrency(val) {
    if (val === null || val === undefined || isNaN(val)) return "$0.00";
    return "$" + Number(val).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  async function copyQuoteSummary() {
    if (!isCompanySelected) return;
    let text = `==============================\n`;
    text += `TRUCKTRACK FORMAL CUSTOMER QUOTE\n`;
    text += `Quote #: ${$customerQuote.quoteNumber || ($customerQuote.id ? `QT-${$customerQuote.id}` : "Draft")}\n`;
    text += `Date: ${$customerQuote.quoteDate} (Valid until ${$customerQuote.validUntil})\n`;
    text += `==============================\n\n`;

    text += `CUSTOMER DETAILS:\n`;
    text += `Client / Company: ${$customerQuote.customerName || "—"}\n`;
    text += `Prepared For: ${$customerQuote.contactPerson || $customerQuote.prepared_for || "—"}\n`;
    text += `Phone: ${formatPhoneNumber($customerQuote.phone) || "—"} | Email: ${$customerQuote.email || "—"}\n\n`;

    if (packages.length === 0) {
      text += `(No calculated haulage package attached yet)\n\n`;
    } else {
      packages.forEach((p, idx) => {
        const fin = calculatePackageFinancials(p);
        const yardName = p.yardName + (p.suburb ? ` (${p.suburb})` : "");
        const cap = p.totalCapacity || (Number(p.truckCapacityTonne || 19) + Number(p.trailerCapacityTonne || 19));
        const pTonnes = fin ? fin.tonnes : 0;
        const pLoads = fin ? fin.loads : 0;
        const pAllocTrucks = fin ? fin.allocatedTrucks : 1;
        const pCalcDays = fin ? fin.calculatedDays : 0;
        const pDaysSplit = getFullAndRemainderDays(pCalcDays);

        text += `--------------------------------------------------\n`;
        text += `MATERIAL ${idx + 1}: ${p.material || "Material"}\n`;
        text += `Pickup Site: ${p.siteAddress || p.load_address?.street || "—"}\n`;
        text += `Disposal Yard: ${yardName}\n`;
        text += `Fleet Configuration: ${cap}t capacity (${p.truckCapacityTonne || 19}t truck + ${p.trailerCapacityTonne || 19}t trailer)\n`;
        text += `Turnaround & Daily Capacity: ${p.runs || 0} runs/pd\n`;
        text += `Job Schedule: ${pLoads} total loads (${Number(pTonnes).toLocaleString()} tonnes total)\n`;
        text += `Allocated Fleet: ${pAllocTrucks} truck${pAllocTrucks > 1 ? 's' : ''}\n`;
        text += `Estimated Duration: ${formatDays(pCalcDays)} (${pDaysSplit.fullDays} full days + ${pDaysSplit.remainderDays} rem)\n`;
        text += `Base Cartage Cost: ${formatCurrency(fin ? fin.baseCartageCost : 0)}\n`;
        text += `Base Tolls: ${formatCurrency(fin ? fin.baseTolls : 0)}\n`;
        text += `Material Subtotal (ex GST): ${formatCurrency(fin ? fin.subtotal : 0)}\n`;
        text += `Client Rate (inc GST): ${formatCurrency(fin ? fin.clientRate : 0)} / tonne\n`;
      });
      text += `--------------------------------------------------\n\n`;

      if (packages.length > 1) {
        text += `==============================\n`;
        text += `QUOTE GRAND TOTAL (ALL MATERIALS):\n`;
        text += `Combined Base Haulage & Disposal: ${formatCurrency(combinedBaseCost)}\n`;
        text += `Total Margins Added: +${formatCurrency(combinedMarginAmount)}\n`;
        text += `Combined Subtotal (ex GST): ${formatCurrency(combinedSubtotal)}\n`;
        text += `GST (10%): ${formatCurrency(combinedGst)}\n`;
        text += `GRAND TOTAL QUOTE (inc GST): ${formatCurrency(combinedGrandTotal)}\n`;
        if (combinedTonnes > 0) {
          text += `AVERAGE CLIENT RATE: ${formatCurrency(combinedAverageRate)} / tonne (${Number(combinedTonnes).toLocaleString()} tonnes total)\n`;
        }
        text += `==============================\n\n`;
      } else {
        text += `PRICING BREAKDOWN:\n`;
        text += `Base Haulage & Disposal: ${formatCurrency(baseTotalCost)}\n`;
        text += `Total Margin Added: +${formatCurrency(marginAmount)}\n`;
        text += `Subtotal (ex GST): ${formatCurrency(subtotal)}\n`;
        text += `GST (10%): ${formatCurrency(gst)}\n`;
        text += `TOTAL QUOTE (inc GST): ${formatCurrency(totalIncGst)}\n`;
        if (tonnes > 0) {
          text += `CLIENT EFFECTIVE RATE: ${formatCurrency(ratePerTonne)} / tonne\n`;
        }
        text += `==============================\n\n`;
      }
    }

    text += `TERMS & CONDITIONS:\n`;
    text += `${$customerQuote.notes}\n`;

    try {
      await navigator.clipboard.writeText(text);
      copyFeedback = true;
      if (copyFeedbackTimeout) clearTimeout(copyFeedbackTimeout);
      copyFeedbackTimeout = setTimeout(() => {
        copyFeedback = false;
      }, 2500);
    } catch (err) {
      console.error("Failed to copy quote text:", err);
    }
  }

  function printQuote() {
    if (!isCompanySelected) return;
    try {
      showCustomerDetails = true;

      const items = (allPackagesFinancials && allPackagesFinancials.length > 0)
        ? allPackagesFinancials.map(f => ({
            material: f.material,
            yardName: f.yardName,
            suburb: f.suburb,
            tonnes: f.tonnes,
            loads: f.loads,
            allocatedTrucks: f.allocatedTrucks,
            calculatedDays: f.calculatedDays,
            subtotal: f.subtotal,
            gst: f.gst,
            totalIncGst: f.totalIncGst,
            clientRate: f.clientRate
          }))
        : [{
            material: activeTabName || "General Material",
            yardName: pkg ? pkg.yardName : "",
            suburb: pkg ? pkg.suburb : "",
            tonnes: tonnes || 1,
            loads: totalLoads || 1,
            allocatedTrucks: allocatedTrucks || 1,
            calculatedDays: calculatedDays || 1,
            subtotal: subtotal || 0,
            gst: gst || 0,
            totalIncGst: totalIncGst || 0,
            clientRate: ratePerTonne || 0
          }];

      generateQuotePdf($customerQuote, {
        items,
        combinedSubtotal: hasHaulage ? combinedSubtotal : (subtotal || 0),
        combinedGst: hasHaulage ? combinedGst : (gst || 0),
        combinedGrandTotal: hasHaulage ? combinedGrandTotal : (totalIncGst || 0),
        combinedTonnes: hasHaulage ? combinedTonnes : (tonnes || 0),
        combinedAverageRate: hasHaulage ? combinedAverageRate : (ratePerTonne || 0),
        operatingHours: (pkg && pkg.startTime && pkg.finishTime)
          ? `Operating Hours: ${pkg.startTime} – ${pkg.finishTime} (Mon – Fri)`
          : 'Standard Operating Hours: 07:00 – 17:00'
      });

      pdfFeedback = true;
      if (pdfFeedbackTimeout) clearTimeout(pdfFeedbackTimeout);
      pdfFeedbackTimeout = setTimeout(() => {
        pdfFeedback = false;
      }, 2500);
    } catch (err) {
      console.error("Failed to generate PDF quote:", err);
      // Fallback to browser print if error occurs
      window.print();
    }
  }
</script>

<div class="customer-quote-window {isPage ? 'is-page-view' : ''}">
  <!-- Card Header -->
  <div class="card-header">
    <div class="header-info">
      <div class="header-title-row">
        <h2 class="card-title">Customer Quote</h2>
        <span class="quote-number-tag">{$customerQuote.quoteNumber || ($customerQuote.id ? `QT-${$customerQuote.id}` : "Draft")}</span>
        {#if $customerQuote.id}
          <span class="quote-id-tag" title="Database Quote ID">ID #{$customerQuote.id}</span>
        {/if}
        {#if hasHaulage}
          <span class="tt-badge tt-badge-success">Ready</span>
        {:else}
          <span class="tt-badge tt-badge-slate">Draft</span>
        {/if}
      </div>
      <span class="card-subtitle">Compose client proposal and attach optimized haulage rates</span>
    </div>

    <div class="header-actions">
      <button
        class="header-btn"
        on:click={resetQuote}
        title="Start fresh new quote"
        type="button"
      >
        <DocumentAdd size={18} />
        {#if isPage}<span class="btn-text-label">New Quote</span>{/if}
      </button>

      <button
        class="header-btn"
        on:click={handleGoToQuote}
        disabled={!isCompanySelected || !$customerQuote.id || isSavingQuote}
        title={!isCompanySelected ? "Please select a company first" : (!$customerQuote.id ? "Please save the quote first to open in Quote Details" : "Open in Quote Details")}
        type="button"
      >
        <Document size={18} />
        {#if isPage}<span class="btn-text-label">Go to Quote</span>{/if}
      </button>

      <button
        class="header-btn"
        on:click={handleSaveQuote}
        disabled={!isCompanySelected || isSavingQuote}
        title={!isCompanySelected ? "Please select a company before saving quote" : ($customerQuote.id ? `Update Quote #${$customerQuote.id} in database` : "Save quote to database")}
        type="button"
      >
        {#if isSavingQuote}
          <span class="header-spinner" />
        {:else if saveFeedbackType === 'success' && saveFeedbackMessage}
          <Checkmark size={18} style="color: var(--tt-success);" />
        {:else}
          <Save size={18} />
        {/if}
        {#if isPage}<span class="btn-text-label">Save Quote</span>{/if}
      </button>

      <button
        class="header-btn"
        on:click={copyQuoteSummary}
        disabled={!isCompanySelected}
        title={!isCompanySelected ? "Please select a company to copy quote" : "Copy formal quote to clipboard"}
        type="button"
      >
        {#if copyFeedback}
          <Checkmark size={18} style="color: var(--tt-success);" />
        {:else}
          <Copy size={18} />
        {/if}
        {#if isPage}<span class="btn-text-label">Copy</span>{/if}
      </button>

      <button
        class="header-btn"
        on:click={printQuote}
        disabled={!isCompanySelected}
        title={!isCompanySelected ? "Please select a company to download PDF" : "Download official PDF quote"}
        type="button"
      >
        {#if pdfFeedback}
          <Checkmark size={18} style="color: var(--tt-success);" />
        {:else}
          <Printer size={18} />
        {/if}
        {#if isPage}<span class="btn-text-label">PDF Quote</span>{/if}
      </button>

      {#if !isPage}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <button
          class="close-card-btn"
          on:click={closeThisWindow}
          title="Close customer quote window"
          type="button"
        >
          <ChevronRight size={20} />
        </button>
      {/if}
    </div>
  </div>

  <!-- Card Body -->
  <div class="card-body">
    <!-- Feedback banner for Save -->
    {#if saveFeedbackMessage}
      <div class="feedback-toast {saveFeedbackType}">
        {#if saveFeedbackType === 'success'}
          <Checkmark size={14} />
        {:else}
          <WarningFilled size={14} />
        {/if}
        <span>{saveFeedbackMessage}</span>
      </div>
    {/if}

    <!-- Feedback banner for copy -->
    {#if copyFeedback}
      <div class="feedback-toast success">
        <Checkmark size={14} />
        <span>Full quote summary copied to clipboard!</span>
      </div>
    {/if}

    <!-- Haulage Status Banner -->
    {#if !hasHaulage}
      <div class="quote-banner notice">
        <span class="banner-icon">ℹ️</span>
        <div class="banner-content">
          <strong>No haulage attached yet:</strong> You can fill in customer details anytime. When ready, click <strong>+ Quote</strong> on any yard in the Haulage Calculator to attach its pricing here.
        </div>
      </div>
    {:else}
      <div class="quote-banner success">
        <span class="banner-icon">✓</span>
        <div class="banner-content">
          <strong>Haulage package attached:</strong> Using rates from <strong>{$customerQuote.haulagePackage.yardName}</strong> ({dailyRuns} runs/pd, {totalLoads} loads, {formatDays(calculatedDays)} with {allocatedTrucks} truck{allocatedTrucks > 1 ? 's' : ''}, {formatCurrency(subtotal)}).
        </div>
      </div>
    {/if}

    <!-- SECTION 1: Customer Details -->
    <div class="form-section-title">Customer Information</div>

    <!-- Company Search / Selection Box -->
    {#if !selectedCompany}
      <div class="company-search-box-wrap">
        <div class="company-search-label-row">
          <label class="custom-field-label" for="company-search-input">
            Search Client / Company <span class="required-star" title="Company is mandatory">*</span>
          </label>
          <span class="required-pill">Required</span>
        </div>
        <div class="company-search-input-field">
          <Search size={16} class="company-search-icon" />
          <input
            id="company-search-input"
            type="text"
            class="company-search-control"
            placeholder={isLoadingCompanies ? "Loading companies from database..." : "Search company by name, ABN, suburb..."}
            bind:value={companySearchTerm}
            on:focus={() => (isCompanySearchFocused = true)}
            on:input={() => (isCompanySearchFocused = true)}
          />
          {#if companySearchTerm}
            <button
              type="button"
              class="clear-input-icon-btn"
              on:click={() => { companySearchTerm = ""; isCompanySearchFocused = false; }}
              title="Clear search"
            >
              <Close size={14} />
            </button>
          {/if}
        </div>
        <div class="company-required-hint-text">
          <span>A company must be selected before saving or exporting this quote.</span>
        </div>

        {#if isCompanySearchFocused && filteredCompanies.length > 0}
          <div class="company-search-results-dropdown" transition:slide={{ duration: 140 }}>
            {#each filteredCompanies as comp}
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <div
                class="company-search-result-row"
                on:mousedown={() => selectCompany(comp)}
              >
                <div class="result-row-top">
                  <span class="result-company-name">{comp.name}</span>
                  {#if comp.ABN}
                    <span class="result-company-abn">ABN {comp.ABN}</span>
                  {/if}
                </div>
                <div class="result-row-sub">
                  {#if comp.address}
                    <span class="result-company-addr">{inlineAddress(comp.address)}</span>
                  {/if}
                  {#if comp.phone}
                    <span class="result-company-phone">• {formatPhoneNumber(comp.phone)}</span>
                  {/if}
                  {#if comp.contacts && comp.contacts.length > 0}
                    <span class="result-company-contacts">({comp.contacts.length} contact{comp.contacts.length > 1 ? 's' : ''})</span>
                  {/if}
                </div>
              </div>
            {/each}
          </div>
        {:else if isCompanySearchFocused && companySearchTerm.trim().length > 1}
          <div class="company-search-results-dropdown empty-state" transition:slide={{ duration: 140 }}>
            <span class="empty-state-text">No company matching "{companySearchTerm}"</span>
            <button
              type="button"
              class="create-company-inline-btn"
              on:mousedown={() => handleCreateNewCompany(companySearchTerm.trim())}
            >
              + Add "{companySearchTerm.trim()}" to Company Database
            </button>
          </div>
        {/if}
      </div>
    {:else}
      <!-- Selected Company Information (Uneditable fields with Edit button) -->
      <div class="selected-company-card">
        <div class="selected-company-header">
          <div class="company-header-title-wrap">
            <span class="company-header-icon"><Enterprise size={16} /></span>
            <span class="company-header-name">{selectedCompany.name || "Client Company"}</span>
            <span class="company-verified-tag">Company Record</span>
          </div>
          <div class="company-header-actions">
            <button
              type="button"
              class="company-edit-btn"
              on:click={() => (showEditCompanyModal = true)}
              title="Edit company details in company database"
            >
              <Edit size={13} />
              <span>Edit Company</span>
            </button>
            <button
              type="button"
              class="company-change-btn"
              on:click={clearSelectedCompany}
              title="Search and select a different company"
            >
              <span>Change</span>
            </button>
          </div>
        </div>

        <div class="company-uneditable-grid">
          <div class="uneditable-field-item col-span-2">
            <span class="uneditable-label">Client / Company Name</span>
            <div class="uneditable-box">
              <span class="uneditable-value">{selectedCompany.name || "—"}</span>
              <span class="locked-icon" title="Uneditable. Click 'Edit Company' to modify."><Locked size={12} /></span>
            </div>
          </div>

          <div class="uneditable-field-item">
            <span class="uneditable-label">Company Phone</span>
            <div class="uneditable-box">
              <span class="uneditable-value">{formatPhoneNumber(selectedCompany.phone) || selectedCompany.phone || "—"}</span>
              <span class="locked-icon" title="Uneditable. Click 'Edit Company' to modify."><Locked size={12} /></span>
            </div>
          </div>

          <div class="uneditable-field-item">
            <span class="uneditable-label">ABN / ACN</span>
            <div class="uneditable-box">
              <span class="uneditable-value">{selectedCompany.ABN ? `ABN: ${selectedCompany.ABN}` : (selectedCompany.ACN ? `ACN: ${selectedCompany.ACN}` : "—")}</span>
              <span class="locked-icon" title="Uneditable. Click 'Edit Company' to modify."><Locked size={12} /></span>
            </div>
          </div>

          <div class="uneditable-field-item col-span-2">
            <span class="uneditable-label">Registered Office Address</span>
            <div class="uneditable-box">
              <span class="uneditable-value">{inlineAddress(selectedCompany.address) || "No registered address recorded"}</span>
              <span class="locked-icon" title="Uneditable. Click 'Edit Company' to modify."><Locked size={12} /></span>
            </div>
          </div>
        </div>
      </div>
    {/if}

    {#if showCustomerDetails}
      <div class="collapsible-customer-fields" transition:slide={{ duration: 180 }}>
        <!-- Prepared For (Contact Person) Field with Contacts Dropdown -->
        <div class="prepared-for-field-wrap">
          <div class="prepared-for-label-row">
            <div class="prepared-for-label-left">
              <label class="custom-field-label" for="prepared-for-input">
                <span>Prepared For</span>
              </label>
              {#if selectedCompany && companyContacts.length > 0}
                <span class="contacts-available-pill">{companyContacts.length} saved contact{companyContacts.length > 1 ? 's' : ''}</span>
              {/if}
            </div>
            <div class="prepared-for-label-right">
              {#if exactContactMatch}
                <span class="contact-matched-badge">✓ Matched Company Contact</span>
              {/if}
              <button
                class="customer-toggle-arrow-btn"
                on:click={() => (showCustomerDetails = false)}
                title="Hide contact and quote details"
                aria-label="Hide contact and quote details"
                type="button"
              >
                <ChevronUp size={16} />
              </button>
            </div>
          </div>

          <div class="prepared-for-input-container">
            <User size={15} class="prepared-for-icon" />
            <input
              id="prepared-for-input"
              type="text"
              class="prepared-for-input"
              disabled={!selectedCompany}
              placeholder={selectedCompany ? "Start typing contact person's name..." : "Select a company above first to choose contacts"}
              bind:value={preparedForInput}
              on:focus={() => (isPreparedForFocused = true)}
              on:input={() => (isPreparedForFocused = true)}
              on:keydown={handlePreparedForKeyDown}
              on:blur={handlePreparedForBlur}
            />
            {#if preparedForInput}
              <button
                type="button"
                tabindex="-1"
                class="clear-input-icon-btn"
                on:click={() => {
                  preparedForInput = "";
                  $customerQuote.contactPerson = "";
                  $customerQuote.prepared_for = "";
                  $customerQuote.phone = "";
                  $customerQuote.email = "";
                  $customerQuote.position = "";
                  isPreparedForFocused = false;
                }}
                title="Clear contact name"
              >
                <Close size={13} />
              </button>
            {/if}
          </div>

          {#if isPreparedForFocused && selectedCompany}
            <div class="contacts-dropdown-list" transition:slide={{ duration: 140 }}>
              {#if filteredContacts.length > 0}
                <div class="contacts-dropdown-header">Company Contacts for {selectedCompany.name}</div>
                {#each filteredContacts as c}
                  <!-- svelte-ignore a11y-click-events-have-key-events -->
                  <div
                    class="contact-dropdown-row {c.name?.toLowerCase() === preparedForInput.toLowerCase() ? 'active-contact' : ''}"
                    on:mousedown={() => selectContact(c)}
                  >
                    <div class="contact-row-primary">
                      <span class="contact-name">{c.name}</span>
                      {#if c.position}
                        <span class="contact-pos-badge">{c.position}</span>
                      {/if}
                      {#if c.main}
                        <span class="contact-main-badge">Primary</span>
                      {/if}
                    </div>
                    <div class="contact-row-details">
                      {#if c.phone}
                        <span class="contact-detail-item">{formatPhoneNumber(c.phone)}</span>
                      {/if}
                      {#if c.email}
                        <span class="contact-detail-item">• {c.email}</span>
                      {/if}
                    </div>
                  </div>
                {/each}
              {/if}

              {#if preparedForInput.trim().length > 0 && !exactContactMatch}
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <div
                  class="add-new-contact-row"
                  on:mousedown={() => {
                    isPreparedForFocused = false;
                    promptSaveContactDetails(preparedForInput.trim());
                  }}
                >
                  <span class="add-plus-badge">+</span>
                  <div class="add-new-contact-info">
                    <strong>Add "{preparedForInput.trim()}" to Company Contacts</strong>
                    <span>Click to save phone, email and position for this person</span>
                  </div>
                </div>
              {/if}
            </div>
          {/if}
        </div>

        <div class="form-grid-2">
          <div class="form-field">
            <div class="field-label-row">
              <label class="custom-field-label" for="customer-quote-phone">Contact Phone</label>
            </div>
            <PhoneInput
              id="customer-quote-phone"
              bind:value={$customerQuote.phone}
              placeholder="0412 345 678"
            />
          </div>
          <div class="form-field">
            <TextInput
              light
              size="sm"
              labelText="Contact Email"
              placeholder="contact@company.com.au"
              bind:value={$customerQuote.email}
            />
          </div>
        </div>

        <div class="form-grid-2">
          <div class="form-field">
            <TextInput
              light
              size="sm"
              labelText="Quote Date"
              bind:value={$customerQuote.quoteDate}
            />
          </div>
          <div class="form-field">
            <TextInput
              light
              size="sm"
              labelText="Valid Until"
              bind:value={$customerQuote.validUntil}
            />
          </div>
        </div>
      </div>
    {:else}
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div
        class="customer-collapsed-bar"
        on:click={() => (showCustomerDetails = true)}
        title="Click to open contact and quote details"
        transition:slide={{ duration: 150 }}
      >
        <div class="collapsed-summary">
          {#if preparedForInput || $customerQuote.prepared_for || $customerQuote.contactPerson || $customerQuote.phone || $customerQuote.email}
            <span class="collapsed-summary-text">
              {[
                preparedForInput || $customerQuote.prepared_for || $customerQuote.contactPerson,
                formatPhoneNumber($customerQuote.phone),
                $customerQuote.email,
                $customerQuote.quoteDate ? `Quote: ${$customerQuote.quoteDate}` : ''
              ].filter(Boolean).join(' • ')}
            </span>
          {:else}
            <span class="collapsed-summary-placeholder">Contact & quote details collapsed (click to expand)</span>
          {/if}
        </div>
        <button
          class="customer-toggle-arrow-btn"
          on:click|stopPropagation={() => (showCustomerDetails = true)}
          title="Open contact and quote details"
          aria-label="Open contact and quote details"
          type="button"
        >
          <ChevronDown size={16} />
        </button>
      </div>
    {/if}

    <!-- SECTION 2: Selected Haulage & Disposal Package -->
    <div class="package-header-row">
      <div class="form-section-title">
        <span>Selected Haulage & Disposal Package{packages.length > 1 ? 's' : ''}</span>
        {#if packages.length > 1}
          <span class="packages-count-badge">{packages.length} materials</span>
        {/if}
      </div>
      {#if hasHaulage}
        <button
          class="remove-pkg-btn"
          on:click={clearAllPackages}
          title="Clear all packages"
          type="button"
        >
          <TrashCan size={12} />
          <span>Clear All</span>
        </button>
      {/if}
    </div>

    <!-- Material Tabs Navigation Bar -->
    {#if packages.length > 0}
      <div class="material-tabs-container">
        <div class="material-tabs-list" role="tablist">
          {#each packages as p, idx (p.id)}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <div
              class="material-tab-item {p.id === activeTabId ? 'is-active' : ''}"
              role="tab"
              tabindex="0"
              aria-selected={p.id === activeTabId}
              on:click={() => switchTab(p)}
              on:keydown={(e) => { if (e.key === 'Enter' || e.key === ' ') switchTab(p); }}
            >
              <span class="tab-indicator-dot"></span>
              <span class="tab-material-title">{p.material || `Material ${idx + 1}`}</span>
              {#if p.yardName}
                <span class="tab-yard-subtitle">({p.yardName})</span>
              {/if}
              {#if getPkgFinancials(p)}
                <span class="tab-material-price">
                  {formatCurrency(getPkgFinancials(p).totalIncGst || 0)}
                  <span class="tab-price-subtext">(inc GST)</span>
                </span>
              {/if}
              {#if packages.length > 1}
                <button
                  type="button"
                  class="tab-close-icon"
                  title="Remove {p.material || `Material ${idx + 1}`} from quote"
                  aria-label="Remove {p.material || `Material ${idx + 1}`}"
                  on:click|stopPropagation={() => removePackage(p.id)}
                >
                  ✕
                </button>
              {/if}
            </div>
          {/each}
        </div>
      </div>
    {/if}

    {#if hasHaulage && pkg}
      <div class="attached-package-card {pkg.isCheapest ? 'is-best-deal' : ''}">
        <!-- Site / Delivery Location Sub-section -->
        <div class="pkg-sub-section site-delivery-sub-section">
          <div class="pkg-sub-title">
            <div class="site-sub-title-left">
              <span class="site-sub-badge">Material Pickup Site</span>
            </div>

            <div class="pkg-material-chip">
              {pkg.material || $quoteSelectedMaterial || "Disposal"}
            </div>
          </div>
          <div class="site-delivery-input-wrap">
            <TextInput
              light
              size="sm"
              hideLabel
              labelText="Material Pickup Site"
              placeholder="e.g. Church St, Parramatta (street number optional)"
              bind:value={pkg.siteAddress}
              invalid={Boolean(pkg.siteAddress && !isFullAddress(pkg.siteAddress))}
              invalidText="Full address required: MUST include street and suburb (e.g. Church St, Parramatta). Street number is optional."
              on:input={() => {
                if (pkg) {
                  const val = validateFullAddress(pkg.siteAddress);
                  pkg.load_address = (typeof pkg.load_address === "object" && pkg.load_address !== null)
                    ? { ...pkg.load_address, street: pkg.siteAddress, ...(val.valid && val.suburb ? { suburb: val.suburb } : {}) }
                    : { street: pkg.siteAddress, ...(val.valid && val.suburb ? { suburb: val.suburb } : {}) };
                  $customerQuote = $customerQuote;
                }
              }}
            />
            {#if pkg.siteAddress && !isFullAddress(pkg.siteAddress)}
              <div class="site-address-alert">
                <WarningFilled size={14} />
                <span>This must be the full address, ie; <strong>MUST have street and suburb</strong> (e.g. <em>Church St, Parramatta</em> or <em>123 Church St, Parramatta</em>). Street number is optional.</span>
              </div>
            {/if}
          </div>
        </div>

        <!-- Yard Brand & Disposal Address Section (below Material Pickup Site) -->
        <div class="pkg-sub-section yard-delivery-sub-section">
          <div class="pkg-yard-brand">
            <div class="pkg-avatar-wrap">
              {#if pkg.image}
                <img
                  class="pkg-avatar"
                  src={"./assests/yards/" + pkg.image}
                  alt=""
                  on:error={(e) => {
                    reportYardImageError({ id: pkg.yardId, name: pkg.yardName }, "not_found", "./assests/yards/" + pkg.image);
                    e.currentTarget.style.display = "none";
                    const fb = e.currentTarget.parentElement.querySelector(".placeholder-avatar");
                    if (fb) fb.style.display = "flex";
                  }}
                />
                <div class="pkg-avatar placeholder-avatar" style="display: none;">🏭</div>
              {:else}
                <div class="pkg-avatar placeholder-avatar">🏭</div>
              {/if}
            </div>
            <div class="pkg-identity">
              <div class="pkg-name-row" style="display: flex; align-items: center; gap: 6px; flex-wrap: wrap;">
                <span class="pkg-name">{pkg.site_name || pkg.yardName}</span>
                {#if pkg.destination_type === 'ad_hoc_site'}
                  <span style="font-size: 10px; font-weight: 700; background: #e0e7ff; color: #3730a3; padding: 2px 6px; border-radius: 4px; text-transform: uppercase;">Custom Ad-Hoc Site</span>
                {:else if pkg.destination_type === 'private_site'}
                  <span style="font-size: 10px; font-weight: 700; background: #ecfdf5; color: #065f46; padding: 2px 6px; border-radius: 4px; text-transform: uppercase;">Private Site</span>
                {:else}
                  <span style="font-size: 10px; font-weight: 700; background: #f1f5f9; color: #475569; padding: 2px 6px; border-radius: 4px; text-transform: uppercase;">Commercial Yard</span>
                {/if}
                {#if pkg.disposal_fee_type === 'per_load'}
                  <span style="font-size: 10px; font-weight: 700; background: #fef3c7; color: #92400e; padding: 2px 6px; border-radius: 4px;">Per Load ({pkg.disposal_loads_per_trip || 1} loads/trip)</span>
                {/if}
              </div>
              <span class="pkg-suburb">{pkg.suburb || "NSW"}</span>
            </div>
          </div>

          <div class="disposal-address-input-wrap">
            <TextInput
              light
              size="sm"
              hideLabel
              labelText="Disposal Site Address"
              placeholder="e.g. Yard / Disposal Address"
              bind:value={pkg.disposalAddress}
              on:input={() => {
                if (pkg) {
                  pkg.disposal_address = (typeof pkg.disposal_address === "object" && pkg.disposal_address !== null)
                    ? { ...pkg.disposal_address, street: pkg.disposalAddress }
                    : { street: pkg.disposalAddress };
                  pkg.dispose_address = pkg.disposalAddress;
                  $customerQuote = $customerQuote;
                }
              }}
            />
          </div>
        </div>

        <!-- Operating Hours & Durations Section -->
        <div class="pkg-sub-section">
          <div class="pkg-sub-title">Operating Hours & Durations</div>
          <div class="pkg-spec-grid-5">
            <div class="pkg-spec-item">
              <span class="spec-label">Total Tonnes</span>
              <div class="tonnes-inline-control">
                <input
                  type="number"
                  class="tonnes-inline-input"
                  min="0"
                  step="50"
                  value={pkg.totalTonnes != null ? pkg.totalTonnes : (tonnes || 0)}
                  on:input={handleTonnesInput}
                  title="Total material tonnes (automatically updates required loads and days)"
                />
                <span class="tonnes-unit-label">t</span>
              </div>
            </div>
            <div class="pkg-spec-item">
              <span class="spec-label">Start (24h)</span>
              <span class="spec-value">{pkg.startTime || '07:00'}</span>
            </div>
            <div class="pkg-spec-item">
              <span class="spec-label">Finish (24h)</span>
              <span class="spec-value">{pkg.finishTime || '17:00'}</span>
            </div>
            <div class="pkg-spec-item">
              <span class="spec-label">Load (mins)</span>
              <span class="spec-value">{pkg.loadTime != null ? pkg.loadTime : 10}</span>
            </div>
            <div class="pkg-spec-item">
              <span class="spec-label">Tip (mins)</span>
              <span class="spec-value">{pkg.tipTime != null ? pkg.tipTime : 10}</span>
            </div>
          </div>
        </div>

        <!-- Fleet Specifications & Rates Section -->
        <div class="pkg-sub-section">
          <div class="pkg-sub-title">Fleet Specifications & Rates</div>
          <div class="pkg-spec-grid-4">
            <div class="pkg-spec-item">
              <span class="spec-label">Truck (t)</span>
              <span class="spec-value">{pkg.truckCapacityTonne || 19} t</span>
            </div>
            <div class="pkg-spec-item">
              <span class="spec-label">Trailer (t)</span>
              <span class="spec-value">{pkg.trailerCapacityTonne != null ? pkg.trailerCapacityTonne : 19} t</span>
            </div>
            <div class="pkg-spec-item">
              <span class="spec-label">Total Capacity</span>
              <span class="spec-value">{pkg.totalCapacity || (Number(pkg.truckCapacityTonne || 19) + Number(pkg.trailerCapacityTonne || 19))} t</span>
            </div>
            <div class="pkg-spec-item">
              <span class="spec-label">Day Rate ($)</span>
              <span class="spec-value">{formatCurrency(pkg.dayRate || 1500)}</span>
            </div>
          </div>
        </div>

        <!-- Turnaround, Schedule & Fleet Allocation Section -->
        <div class="pkg-sub-section highlight-schedule-section">
          <div class="pkg-sub-title">Turnaround, Schedule & Fleet Allocation</div>
          <div class="pkg-spec-grid-4">
            <div class="pkg-spec-item">
              <span class="spec-label">Runs / pd</span>
              <span class="spec-value">{dailyRuns > 0 ? `${dailyRuns} runs` : '—'}</span>
            </div>
            <div class="pkg-spec-item">
              <span class="spec-label">Total Loads</span>
              <span class="spec-value">{totalLoads > 0 ? `${totalLoads.toLocaleString()} loads` : '—'}</span>
            </div>
            <div class="pkg-spec-item days-spec-item">
              <span class="spec-label">Days</span>
              <div class="days-display-group">
                <span class="spec-value days-val-highlight">{calculatedDays > 0 ? formatDays(calculatedDays) : '0 days'}</span>
                {#if allocatedTrucks > 1 && totalDays1Truck > 0}
                  <span class="single-truck-note" title="Duration with 1 single truck">({formatDays(totalDays1Truck)} w/ 1 truck)</span>
                {/if}
              </div>
            </div>
            <div class="pkg-spec-item trucks-spec-item">
              <span class="spec-label">Allocated Trucks</span>
              <div class="trucks-stepper-control">
                <input
                  type="number"
                  class="trucks-stepper-input"
                  min="1"
                  max="50"
                  step="1"
                  value={allocatedTrucks}
                  on:input={handleTrucksInput}
                  title="Number of trucks allocated to this job"
                />
                <div class="stepper-arrow-buttons">
                  <button
                    type="button"
                    class="truck-step-btn step-up"
                    on:click={incrementTrucks}
                    title="Increase truck count"
                    aria-label="Increase truck count"
                  >
                    <ChevronUp size={11} />
                  </button>
                  <button
                    type="button"
                    class="truck-step-btn step-down"
                    on:click={decrementTrucks}
                    disabled={allocatedTrucks <= 1}
                    title="Decrease truck count"
                    aria-label="Decrease truck count"
                  >
                    <ChevronDown size={11} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    {:else}
      <div class="empty-pkg-placeholder">
        <DocumentAdd size={24} style="color: var(--tt-slate-400);" />
        <div class="placeholder-text">
          <strong>No Haulage Package Attached</strong>
          <span>Calculate haulage in the Haulage Calculator, then click <strong>+ Quote</strong> on any yard to attach its rates.</span>
        </div>
      </div>
    {/if}

    <!-- SECTION 3: Pricing & Margin Calculation Matrix -->
    <div class="pricing-matrix-card">
      <div class="matrix-card-header">
        <div class="matrix-title-group">
          <span class="matrix-title">Quote Calculation & Margin Matrix</span>
          <span class="matrix-subtitle">Add percentage (%) or dollar ($) margin to any field</span>
        </div>
        <div class="matrix-presets-row">
          <button
            type="button"
            class="preset-chip reset"
            on:click={resetMargins}
            title="Clear all margins"
          >Reset</button>
        </div>
      </div>

      <div class="matrix-table-wrap">
        <table class="margin-matrix-table">
          <thead>
            <tr>
              <th class="matrix-col-header row-label-col">
                <span class="col-head-main">Rate</span>
                <span class="col-head-sub">&nbsp;</span>
              </th>
              <th class="matrix-col-header">
                <span class="col-head-main">Disposal Rate</span>
                <span class="col-head-sub">$/tonne</span>
              </th>
              <th class="matrix-col-header">
                <span class="col-head-main">Cost / Load</span>
                <span class="col-head-sub">$ / load</span>
              </th>
              <th class="matrix-col-header">
                <span class="col-head-main">Truck Day Rate</span>
                <span class="col-head-sub">$ / day</span>
              </th>
              <th class="matrix-col-header">
                <span class="col-head-main">Total Cartage</span>
                <span class="col-head-sub">Total $</span>
              </th>
              <th class="matrix-col-header">
                <span class="col-head-main">Total Tolls</span>
                <span class="col-head-sub">Total $</span>
              </th>
              <th class="matrix-col-header col-highlight">
                <span class="col-head-main">Total</span>
                <span class="col-head-sub">Subtotal $</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <!-- ROW 1: Base Calculation -->
            <tr class="matrix-row base-row">
              <td class="matrix-cell row-header-cell">
                <span class="row-badge base-badge">Base Rate</span>
              </td>
              <td class="matrix-cell text-right">
                <span class="val-text base-val">{formatCurrency(baseTonneRate)}/t</span>
              </td>
              <td class="matrix-cell text-right">
                <span class="val-text base-val">{formatCurrency(baseCostPerLoad)}</span>
              </td>
              <td class="matrix-cell text-right">
                <span class="val-text base-val">{formatCurrency(baseDayRate)}</span>
              </td>
              <td class="matrix-cell text-right">
                <span class="val-text base-val">{formatCurrency(baseCartageCost)}</span>
              </td>
              <td class="matrix-cell text-right">
                <span class="val-text base-val">{formatCurrency(baseTolls)}</span>
              </td>
              <td class="matrix-cell text-right col-highlight">
                <span class="val-text base-val bold">{formatCurrency(baseTotalCost)}</span>
              </td>
            </tr>

            <!-- ROW 2: Margin Input (+/-) -->
            <tr class="matrix-row margin-input-row">
              <td class="matrix-cell row-header-cell">
                <span class="row-badge margin-badge">Margin (+)</span>
              </td>

              <!-- Disposal Rate Margin -->
              <td class="matrix-cell text-center">
                <div class="margin-cell-control">
                  <button
                    type="button"
                    class="unit-toggle-pill {mTR?.mode === 'dollar' ? 'is-dollar' : 'is-percent'}"
                    on:click={() => toggleMarginMode('tonneRate')}
                    title="Toggle between % and $"
                  >
                    <span class="toggle-opt opt-pct {mTR?.mode === 'percent' ? 'active' : ''}">%</span>
                    <span class="toggle-opt opt-dlr {mTR?.mode === 'dollar' ? 'active' : ''}">$</span>
                  </button>
                  <div class="input-with-affix">
                    <input
                      type="number"
                      class="matrix-number-input"
                      step={mTR?.mode === 'dollar' ? '1' : '0.5'}
                      bind:value={currentMargins.tonneRate.value}
                      on:input={() => { $customerQuote = $customerQuote; }}
                      placeholder="0"
                    />
                    <span class="affix-label">{mTR?.mode === 'dollar' ? '$' : '%'}</span>
                  </div>
                </div>
              </td>

              <!-- Cost Per Load Margin -->
              <td class="matrix-cell text-center">
                <div class="margin-cell-control">
                  <button
                    type="button"
                    class="unit-toggle-pill {mCPL?.mode === 'dollar' ? 'is-dollar' : 'is-percent'}"
                    on:click={() => toggleMarginMode('costPerLoad')}
                    title="Toggle between % and $"
                  >
                    <span class="toggle-opt opt-pct {mCPL?.mode === 'percent' ? 'active' : ''}">%</span>
                    <span class="toggle-opt opt-dlr {mCPL?.mode === 'dollar' ? 'active' : ''}">$</span>
                  </button>
                  <div class="input-with-affix">
                    <input
                      type="number"
                      class="matrix-number-input"
                      step={mCPL?.mode === 'dollar' ? '5' : '0.5'}
                      bind:value={currentMargins.costPerLoad.value}
                      on:input={() => { $customerQuote = $customerQuote; }}
                      placeholder="0"
                    />
                    <span class="affix-label">{mCPL?.mode === 'dollar' ? '$' : '%'}</span>
                  </div>
                </div>
              </td>

              <!-- Day Rate Margin -->
              <td class="matrix-cell text-center">
                <div class="margin-cell-control">
                  <button
                    type="button"
                    class="unit-toggle-pill {mDR?.mode === 'dollar' ? 'is-dollar' : 'is-percent'}"
                    on:click={() => toggleMarginMode('dayRate')}
                    title="Toggle between % and $"
                  >
                    <span class="toggle-opt opt-pct {mDR?.mode === 'percent' ? 'active' : ''}">%</span>
                    <span class="toggle-opt opt-dlr {mDR?.mode === 'dollar' ? 'active' : ''}">$</span>
                  </button>
                  <div class="input-with-affix">
                    <input
                      type="number"
                      class="matrix-number-input"
                      step={mDR?.mode === 'dollar' ? '10' : '0.5'}
                      bind:value={currentMargins.dayRate.value}
                      on:input={() => { $customerQuote = $customerQuote; }}
                      placeholder="0"
                    />
                    <span class="affix-label">{mDR?.mode === 'dollar' ? '$' : '%'}</span>
                  </div>
                </div>
              </td>

              <!-- Cartage Cost Margin -->
              <td class="matrix-cell text-center">
                <div class="margin-cell-control">
                  <button
                    type="button"
                    class="unit-toggle-pill {mCC?.mode === 'dollar' ? 'is-dollar' : 'is-percent'}"
                    on:click={() => toggleMarginMode('cartageCost')}
                    title="Toggle between % and $"
                  >
                    <span class="toggle-opt opt-pct {mCC?.mode === 'percent' ? 'active' : ''}">%</span>
                    <span class="toggle-opt opt-dlr {mCC?.mode === 'dollar' ? 'active' : ''}">$</span>
                  </button>
                  <div class="input-with-affix">
                    <input
                      type="number"
                      class="matrix-number-input"
                      step={mCC?.mode === 'dollar' ? '50' : '0.5'}
                      bind:value={currentMargins.cartageCost.value}
                      on:input={() => { $customerQuote = $customerQuote; }}
                      placeholder="0"
                    />
                    <span class="affix-label">{mCC?.mode === 'dollar' ? '$' : '%'}</span>
                  </div>
                </div>
              </td>

              <!-- Tolls Margin -->
              <td class="matrix-cell text-center">
                <div class="margin-cell-control">
                  <button
                    type="button"
                    class="unit-toggle-pill {mTL?.mode === 'dollar' ? 'is-dollar' : 'is-percent'}"
                    on:click={() => toggleMarginMode('totalTolls')}
                    title="Toggle between % and $"
                  >
                    <span class="toggle-opt opt-pct {mTL?.mode === 'percent' ? 'active' : ''}">%</span>
                    <span class="toggle-opt opt-dlr {mTL?.mode === 'dollar' ? 'active' : ''}">$</span>
                  </button>
                  <div class="input-with-affix">
                    <input
                      type="number"
                      class="matrix-number-input"
                      step={mTL?.mode === 'dollar' ? '10' : '0.5'}
                      bind:value={currentMargins.totalTolls.value}
                      on:input={() => { $customerQuote = $customerQuote; }}
                      placeholder="0"
                    />
                    <span class="affix-label">{mTL?.mode === 'dollar' ? '$' : '%'}</span>
                  </div>
                </div>
              </td>

              <!-- Total Cost Margin -->
              <td class="matrix-cell text-center col-highlight">
                <div class="margin-cell-control">
                  <button
                    type="button"
                    class="unit-toggle-pill {mTC?.mode === 'dollar' ? 'is-dollar' : 'is-percent'}"
                    on:click={() => toggleMarginMode('totalCost')}
                    title="Toggle between % and $"
                  >
                    <span class="toggle-opt opt-pct {mTC?.mode === 'percent' ? 'active' : ''}">%</span>
                    <span class="toggle-opt opt-dlr {mTC?.mode === 'dollar' ? 'active' : ''}">$</span>
                  </button>
                  <div class="input-with-affix">
                    <input
                      type="number"
                      class="matrix-number-input"
                      step={mTC?.mode === 'dollar' ? '25' : '1'}
                      bind:value={currentMargins.totalCost.value}
                      on:input={() => { $customerQuote = $customerQuote; }}
                      placeholder="0"
                    />
                    <span class="affix-label">{mTC?.mode === 'dollar' ? '$' : '%'}</span>
                  </div>
                </div>
              </td>
            </tr>

            <!-- ROW 3: Customer Quote (Adjusted) -->
            <tr class="matrix-row quote-row">
              <td class="matrix-cell row-header-cell">
                <span class="row-badge quote-badge">Quote Rate</span>
              </td>
              <td class="matrix-cell text-right">
                <span class="val-text quote-val">{formatCurrency(adjustedTonneRate)}/t</span>
              </td>
              <td class="matrix-cell text-right">
                <span class="val-text quote-val">{formatCurrency(adjustedCostPerLoad)}</span>
              </td>
              <td class="matrix-cell text-right">
                <span class="val-text quote-val">{formatCurrency(adjustedDayRate)}</span>
              </td>
              <td class="matrix-cell text-right">
                <span class="val-text quote-val">{formatCurrency(adjustedCartageCost)}</span>
              </td>
              <td class="matrix-cell text-right">
                <span class="val-text quote-val">{formatCurrency(adjustedTolls)}</span>
              </td>
              <td class="matrix-cell text-right col-highlight">
                <span class="val-text quote-val bold-large">{formatCurrency(adjustedTotalCost)}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Financial Totals Summary -->
      <div class="matrix-summary-footer">
        <div class="summary-line">
          <span class="summary-label">Base Cartage Cost:</span>
          <span class="summary-value">{formatCurrency(baseCartageCost)}</span>
        </div>
        <div class="summary-line">
          <span class="summary-label">Base Tolls:</span>
          <span class="summary-value">{formatCurrency(baseTolls)}</span>
        </div>
        <div class="summary-line">
          <span class="summary-label">Base Haulage & Disposal:</span>
          <span class="summary-value">{formatCurrency(baseTotalCost)}</span>
        </div>
        <div class="summary-line">
          <span class="summary-label">Total Margin Added:</span>
          <span class="summary-value margin-added">+{formatCurrency(marginAmount)}</span>
        </div>
        <div class="summary-line subtotal-line">
          <span class="summary-label">Subtotal (Excl. GST):</span>
          <span class="summary-value">{formatCurrency(subtotal)}</span>
        </div>
        <div class="summary-line">
          <span class="summary-label">GST (10%):</span>
          <span class="summary-value">+{formatCurrency(gst)}</span>
        </div>
        <div class="summary-line grand-total-line">
          <div class="total-label-wrap">
            <span class="grand-label">Total Quote (Incl. GST):</span>
            {#if tonnes > 0}
              <span class="effective-rate-pill">
                Client Rate: <strong>{formatCurrency(ratePerTonne)} / tonne</strong>
              </span>
            {/if}
          </div>
          <span class="grand-total-val">{formatCurrency(totalIncGst)}</span>
        </div>
      </div>
    </div>

    <!-- Combined Summary Card (Shown when quote has multiple materials) -->
    {#if packages.length > 1}
      <div class="combined-summary-card">
        <div class="combined-card-header">
          <div class="combined-title-wrap">
            <span class="combined-main-title">Quote Grand Total (All Quoted Materials)</span>
            <span class="combined-sub-title">Consolidated breakdown across all {packages.length} materials</span>
          </div>
        </div>

        <div class="combined-table-wrap">
          <table class="combined-materials-table">
            <thead>
              <tr>
                <th>Material</th>
                <th>Disposal Yard</th>
                <th class="text-right">Tonnes</th>
                <th class="text-right">Trucks</th>
                <th class="text-right">Days</th>
                <th class="text-right">Subtotal (Ex GST)</th>
                <th class="text-right">Client Rate (Inc GST)</th>
              </tr>
            </thead>
            <tbody>
              {#each allPackagesFinancials as matFin, fIdx (matFin.id || fIdx)}
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <tr
                  class="combined-row {matFin.id === activeTabId ? 'is-active-tab-row' : ''}"
                  on:click={() => switchTab(packages.find(p => p.id === matFin.id) || packages[fIdx])}
                  title="Click to view/edit {matFin.material}"
                >
                  <td class="bold">
                    <span class="mat-tab-link">{matFin.material}</span>
                  </td>
                  <td class="muted-text">{matFin.yardName}{matFin.suburb ? ` (${matFin.suburb})` : ''}</td>
                  <td class="text-right">{Number(matFin.tonnes).toLocaleString()} t</td>
                  <td class="text-right">{matFin.allocatedTrucks}</td>
                  <td class="text-right">{formatDays(matFin.calculatedDays)}</td>
                  <td class="text-right bold">{formatCurrency(matFin.subtotal)}</td>
                  <td class="text-right client-rate-highlight">{formatCurrency(matFin.clientRate)}/t</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>

        <div class="combined-footer-totals">
          <div class="combined-total-row">
            <span>Combined Base Haulage & Disposal:</span>
            <span>{formatCurrency(combinedBaseCost)}</span>
          </div>
          <div class="combined-total-row">
            <span>Combined Margins Added:</span>
            <span class="margin-added">+{formatCurrency(combinedMarginAmount)}</span>
          </div>
          <div class="combined-total-row subtotal">
            <span>Combined Subtotal (Excl. GST):</span>
            <span>{formatCurrency(combinedSubtotal)}</span>
          </div>
          <div class="combined-total-row">
            <span>Combined GST (10%):</span>
            <span>+{formatCurrency(combinedGst)}</span>
          </div>
          <div class="combined-grand-total-row">
            <div class="combined-grand-label-group">
              <span class="grand-label">Grand Total (Incl. GST):</span>
              {#if combinedTonnes > 0}
                <span class="effective-rate-pill">
                  Average Client Rate: <strong>{formatCurrency(combinedAverageRate)} / tonne</strong> ({Number(combinedTonnes).toLocaleString()} tonnes total)
                </span>
              {/if}
            </div>
            <span class="grand-total-val">{formatCurrency(combinedGrandTotal)}</span>
          </div>
        </div>
      </div>
    {/if}

    <!-- SECTION 4: Terms & Notes -->
    <div class="form-section-title">Terms & Notes</div>
    <div class="form-field">
      <TextArea
        light
        rows={2}
        placeholder="Enter quote notes, conditions or exclusions..."
        bind:value={$customerQuote.notes}
      />
    </div>

    <!-- Actions Bar -->
    {#if saveFeedbackMessage}
      <div class="feedback-toast {saveFeedbackType} footer-toast">
        {#if saveFeedbackType === 'success'}
          <Checkmark size={14} />
        {:else}
          <WarningFilled size={14} />
        {/if}
        <span>{saveFeedbackMessage}</span>
      </div>
    {/if}

    <div class="quote-actions-bar">
      <button
        class="secondary-action-btn"
        on:click={resetQuote}
        type="button"
        title="Start a fresh quote"
      >
        <span>Reset</span>
      </button>

      <div class="action-btn-group">
        <Button
          kind="secondary"
          size="field"
          disabled={!isCompanySelected}
          on:click={copyQuoteSummary}
          title={!isCompanySelected ? "Please select a company to copy quote" : "Copy formal quote to clipboard"}
        >
          {copyFeedback ? "✓ Copied!" : "Copy"}
        </Button>

        <Button
          kind="tertiary"
          size="field"
          disabled={!isCompanySelected}
          on:click={printQuote}
          title={!isCompanySelected ? "Please select a company to download PDF" : "Download official PDF quote"}
        >
          {pdfFeedback ? "✓ PDF Downloaded!" : "Print / PDF"}
        </Button>

        <Button
          kind="secondary"
          size="field"
          disabled={!isCompanySelected || isSavingQuote}
          on:click={handleSaveQuote}
          title={!isCompanySelected ? "Please select a company before saving quote" : ($customerQuote.id ? `Update Quote #${$customerQuote.id} in database` : "Save quote to database")}
        >
          {#if isSavingQuote}
            <span class="btn-spinner"></span>
            <span>Saving...</span>
          {:else if saveFeedbackType === 'success' && saveFeedbackMessage}
            ✓ Saved!
          {:else if saveFeedbackType === 'error' && saveFeedbackMessage}
            ⚠️ Error
          {:else if $customerQuote.id}
            Update Quote
          {:else}
            Save Quote
          {/if}
        </Button>

        <Button
          kind="primary"
          size="field"
          class="go-to-quote-action-btn"
          disabled={!isCompanySelected || !$customerQuote.id || isSavingQuote}
          on:click={handleGoToQuote}
          title={!isCompanySelected ? "Please select a company first" : (!$customerQuote.id ? "Please save the quote first before opening Quote Details" : "Open this quote in Quote Details page")}
        >
          <span>Go to Quote &rarr;</span>
        </Button>
      </div>
    </div>
  </div>
</div>

{#if showEditCompanyModal && selectedCompany}
  <CompanyForm
    bind:company={selectedCompany}
    saveToDB={true}
    on:company-saved={handleCompanySaved}
    on:company-closed={() => (showEditCompanyModal = false)}
  />
{/if}

{#if showSaveContactModal}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div use:portal class="m3-dialog-scrim" role="presentation" on:click|self={handleUseContactWithoutSaving}>
    <div class="m3-dialog-surface save-contact-dialog" role="dialog" aria-modal="true" aria-labelledby="save-contact-title">
      <div class="save-contact-header">
        <div class="save-contact-title-group">
          <div class="save-contact-icon-badge">
            <User size={20} />
          </div>
          <div>
            <h3 id="save-contact-title" class="save-contact-heading">Save Contact to Company</h3>
            <span class="save-contact-subheading">
              Save <strong>{newContactData.name || "this person"}</strong> to <strong>{selectedCompany?.name || "this company"}</strong>?
            </span>
          </div>
        </div>
        <button
          type="button"
          class="dialog-close-icon-btn"
          on:click={handleUseContactWithoutSaving}
          title="Close dialog"
        >
          <Close size={18} />
        </button>
      </div>

      <div class="save-contact-body">
        <div class="form-field contact-form-field">
          <TextInput
            labelText="Full Name"
            placeholder="e.g. Sarah Jenkins"
            bind:value={newContactData.name}
          />
        </div>

        <div class="form-field contact-form-field">
          <TextInput
            labelText="Position / Role"
            placeholder="e.g. Project Manager, Estimator, Site Supervisor"
            bind:value={newContactData.position}
          />
        </div>

        <div class="form-grid-2">
          <div class="form-field">
            <span class="bx--label">Phone Number</span>
            <PhoneInput
              id="new-contact-phone-input"
              bind:value={newContactData.phone}
              placeholder="0412 345 678"
            />
          </div>
          <div class="form-field">
            <TextInput
              labelText="Email Address"
              placeholder="sarah@company.com.au"
              bind:value={newContactData.email}
            />
          </div>
        </div>
      </div>

      <div class="save-contact-footer">
        <button
          type="button"
          class="dialog-btn secondary-btn"
          on:click={handleUseContactWithoutSaving}
        >
          Use for Quote Only
        </button>
        <button
          type="button"
          class="dialog-btn primary-save-btn"
          on:click={handleSaveNewContact}
          disabled={!newContactData.name || !newContactData.name.trim()}
        >
          <Checkmark size={15} />
          <span>Save Contact & Select</span>
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .customer-quote-window {
    position: absolute;
    top: 4rem;
    right: 1rem;
    width: 660px;
    max-width: calc(100vw - 2rem);
    max-height: calc(100% - 5rem);
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(16px);
    border: 1px solid var(--tt-slate-200);
    border-radius: var(--tt-radius-lg);
    box-shadow: var(--tt-shadow-xl);
    z-index: 1200;
    pointer-events: auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: slideInRight 0.22s ease-out;
  }

  .customer-quote-window.is-page-view {
    position: relative;
    top: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
    border: none;
    box-shadow: none;
    animation: none;
    background: #ffffff;
    backdrop-filter: none;
  }

  .customer-quote-window.is-page-view .card-body {
    padding: 1.5rem 2.5rem 4rem;
    max-width: 960px;
    margin: 0 auto;
    width: 100%;
  }

  .btn-text-label {
    margin-left: 6px;
    font-size: 12px;
    font-weight: 600;
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(12px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.875rem 1.125rem;
    border-bottom: 1px solid var(--tt-slate-200);
    background-color: var(--tt-slate-50);
  }

  .header-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .header-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .card-title {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 700;
    color: var(--tt-slate-900);
    letter-spacing: -0.01em;
  }

  .quote-number-tag {
    font-size: 11px;
    font-weight: 700;
    color: var(--tt-primary);
    background-color: rgba(66, 133, 244, 0.1);
    padding: 1px 6px;
    border-radius: 4px;
    border: 1px solid rgba(66, 133, 244, 0.2);
  }

  .quote-id-tag {
    font-size: 11px;
    font-weight: 700;
    color: #16a34a;
    background-color: rgba(22, 163, 74, 0.1);
    padding: 1px 6px;
    border-radius: 4px;
    border: 1px solid rgba(22, 163, 74, 0.25);
  }

  .header-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(66, 133, 244, 0.2);
    border-top-color: var(--tt-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .btn-spinner {
    display: inline-block;
    width: 12px;
    height: 12px;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-right: 6px;
    vertical-align: middle;
  }

  .footer-toast {
    margin-top: 8px;
    margin-bottom: 4px;
    animation: fadeIn 0.15s ease-out;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .card-subtitle {
    font-size: 0.75rem;
    color: var(--tt-slate-500);
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .header-btn,
  .close-card-btn {
    width: 30px;
    height: 30px;
    border-radius: var(--tt-radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid transparent;
    color: var(--tt-slate-500);
    cursor: pointer;
    transition: all 0.15s ease;
    padding: 0;
    margin: 0;
  }

  .header-btn:hover:not(:disabled),
  .close-card-btn:hover:not(:disabled) {
    background: var(--tt-slate-200);
    color: var(--tt-slate-800);
  }

  .header-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    pointer-events: none;
  }

  .card-body {
    padding: 1rem 1.125rem;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    scrollbar-width: thin;
    scrollbar-color: var(--tt-slate-300) transparent;
  }

  .card-body::-webkit-scrollbar {
    width: 5px;
  }

  .card-body::-webkit-scrollbar-thumb {
    background: var(--tt-slate-300);
    border-radius: 4px;
  }

  .feedback-toast {
    display: flex;
    align-items: center;
    gap: 6px;
    background-color: var(--tt-success-bg);
    border: 1px solid var(--tt-success-border);
    color: var(--tt-success-text);
    padding: 6px 10px;
    border-radius: var(--tt-radius-sm);
    font-size: 11.5px;
    font-weight: 600;
    animation: fadeIn 0.15s ease-out;
  }

  .feedback-toast.error {
    background-color: #fef2f2;
    border-color: #fecaca;
    color: #991b1b;
  }

  .feedback-toast.success {
    background-color: var(--tt-success-bg);
    border-color: var(--tt-success-border);
    color: var(--tt-success-text);
  }

  .tt-badge {
    display: inline-flex;
    align-items: center;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 1px 6px;
    border-radius: 4px;
  }

  .tt-badge-success {
    background-color: #ecfdf5;
    color: #047857;
    border: 1px solid #a7f3d0;
  }

  .tt-badge-slate {
    background-color: #f1f5f9;
    color: #475569;
    border: 1px solid #cbd5e1;
  }

  .tt-badge-primary {
    background-color: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .quote-banner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 9px 11px;
    border-radius: var(--tt-radius-sm);
    font-size: 11.5px;
    line-height: 1.4;
  }

  .quote-banner.notice {
    background-color: var(--tt-slate-100);
    border: 1px solid var(--tt-slate-300);
    color: var(--tt-slate-700);
  }

  .quote-banner.success {
    background-color: var(--tt-success-bg);
    border: 1px solid var(--tt-success-border);
    color: var(--tt-success-text);
  }

  .banner-icon {
    font-size: 13px;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .form-section-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tt-slate-500);
    margin-top: 2px;
  }

  .form-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .form-field {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  .customer-quote-window :global(.bx--form-item) {
    margin: 0 !important;
    width: 100% !important;
  }

  .customer-quote-window :global(.bx--label) {
    font-size: 11px !important;
    line-height: 14px !important;
    height: 14px !important;
    margin-bottom: 3px !important;
    color: var(--tt-slate-600) !important;
  }

  .customer-quote-window :global(.bx--text-input--sm) {
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
    font-size: 12.5px !important;
  }

  .customer-quote-window :global(.bx--text-area) {
    font-size: 12px !important;
    min-height: 52px !important;
  }

  .collapsible-customer-fields {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .field-label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 14px;
    margin-bottom: 3px;
  }

  .custom-field-label {
    font-size: 11px;
    line-height: 14px;
    color: var(--tt-slate-600);
    font-weight: 400;
  }

  .customer-toggle-arrow-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 16px;
    border-radius: 3px;
    background: transparent;
    border: 1px solid transparent;
    color: var(--tt-slate-500);
    cursor: pointer;
    padding: 0;
    margin: 0;
    transition: all 0.15s ease;
  }

  .customer-toggle-arrow-btn:hover {
    background: var(--tt-slate-200);
    color: var(--tt-slate-900);
    border-color: var(--tt-slate-300);
  }

  .customer-toggle-arrow-btn:active {
    transform: scale(0.92);
  }

  .customer-collapsed-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 4px 8px 4px 10px;
    background-color: var(--tt-slate-50);
    border: 1px dashed var(--tt-slate-200);
    border-radius: var(--tt-radius-sm);
    cursor: pointer;
    transition: all 0.15s ease;
    margin: 4px 0 10px 0;
  }

  .customer-collapsed-bar:hover {
    background-color: var(--tt-slate-100);
    border-color: var(--tt-slate-300);
  }

  .collapsed-summary {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--tt-slate-600);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    user-select: none;
    flex: 1;
  }

  .collapsed-summary-placeholder {
    color: var(--tt-slate-400);
    font-style: italic;
  }

  .collapsed-summary-text {
    font-weight: 500;
    color: var(--tt-slate-700);
  }

  .package-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .remove-pkg-btn {
    background: transparent;
    border: none;
    color: #ef4444;
    font-size: 11px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    padding: 2px 4px;
    border-radius: 3px;
    transition: background-color 0.12s ease;
  }

  .remove-pkg-btn:hover {
    background-color: #fee2e2;
  }

  .attached-package-card {
    background-color: #ffffff;
    border: 1px solid var(--tt-slate-200);
    border-radius: var(--tt-radius-md);
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  }

  .attached-package-card.is-best-deal {
    border-color: #a7f3d0;
    background: #f0fdf4;
  }

  .pkg-top-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .pkg-yard-brand {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .pkg-avatar-wrap {
    width: 28px;
    height: 28px;
    border-radius: 4px;
    border: 1px solid var(--tt-slate-200);
    overflow: hidden;
    background: var(--tt-slate-100);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .pkg-avatar {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .pkg-identity {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .pkg-name-row {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .pkg-name {
    font-size: 12.5px;
    font-weight: 700;
    color: var(--tt-slate-900);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 190px;
  }

  .pkg-suburb {
    font-size: 11px;
    color: var(--tt-slate-500);
  }

  .best-badge {
    font-size: 10px;
    font-weight: 700;
    color: #047857;
    background-color: #d1fae5;
    border: 1px solid #a7f3d0;
    padding: 0 5px;
    border-radius: 4px;
    white-space: nowrap;
  }

  .pkg-material-chip {
    font-size: 11px;
    font-weight: 700;
    color: var(--tt-slate-700);
    background: var(--tt-slate-100);
    border: 1px solid var(--tt-slate-200);
    padding: 2px 7px;
    border-radius: 9999px;
    white-space: nowrap;
  }

  .pkg-sub-section {
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: rgba(255, 255, 255, 0.75);
    border: 1px solid var(--tt-slate-200);
    border-radius: var(--tt-radius-sm);
    padding: 6px 9px;
  }

  .is-best-deal .pkg-sub-section {
    background: rgba(255, 255, 255, 0.85);
    border-color: #bbf7d0;
  }

  .pkg-sub-title {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tt-slate-500);
    margin-bottom: 2px;
  }

  .pkg-spec-grid-5 {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 4px 8px;
    align-items: start;
  }

  .pkg-spec-grid-4 {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 4px 8px;
    align-items: start;
  }

  .pkg-spec-item {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .spec-label {
    font-size: 9.5px;
    font-weight: 600;
    color: var(--tt-slate-500);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .spec-value {
    font-size: 11.5px;
    font-weight: 700;
    color: var(--tt-slate-800);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .empty-pkg-placeholder {
    border: 1.5px dashed var(--tt-slate-300);
    border-radius: var(--tt-radius-md);
    background-color: var(--tt-slate-50);
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .placeholder-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 11.5px;
    line-height: 1.35;
    color: var(--tt-slate-600);
  }

  .placeholder-text strong {
    color: var(--tt-slate-800);
  }

  /* Pricing & Margin Matrix Card */
  .pricing-matrix-card {
    background: #ffffff;
    border: 1px solid var(--tt-slate-200);
    border-radius: var(--tt-radius-md);
    padding: 8px 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  }

  .matrix-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding-bottom: 6px;
    border-bottom: 1px solid var(--tt-slate-100);
    flex-wrap: wrap;
  }

  .matrix-title-group {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .matrix-title {
    font-size: 12px;
    font-weight: 700;
    color: var(--tt-slate-800);
  }

  .matrix-subtitle {
    font-size: 10.5px;
    color: var(--tt-slate-500);
  }

  .matrix-presets-row {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .preset-label {
    font-size: 10.5px;
    font-weight: 600;
    color: var(--tt-slate-400);
    margin-right: 2px;
  }

  .preset-chip {
    background: var(--tt-slate-100);
    border: 1px solid var(--tt-slate-200);
    color: var(--tt-slate-700);
    font-size: 10.5px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.12s ease;
  }

  .preset-chip:hover {
    background: #dbeafe;
    border-color: #93c5fd;
    color: #1d4ed8;
  }

  .preset-chip.is-active {
    background: #2563eb;
    border-color: #1d4ed8;
    color: #ffffff;
  }

  .preset-chip.reset {
    color: var(--tt-slate-500);
  }

  .preset-chip.reset:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #b91c1c;
  }

  /* Table styling */
  .matrix-table-wrap {
    width: 100%;
    overflow-x: auto;
    border: 1px solid var(--tt-slate-200);
    border-radius: var(--tt-radius-sm);
  }

  .margin-matrix-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
    text-align: left;
    background: #ffffff;
  }

  .matrix-col-header {
    background: var(--tt-slate-50);
    padding: 5px 3px;
    border-bottom: 1.5px solid var(--tt-slate-200);
    font-size: 9px;
    font-weight: 700;
    color: var(--tt-slate-600);
    text-transform: uppercase;
    letter-spacing: 0.01em;
    vertical-align: top;
    text-align: right;
    white-space: nowrap;
  }

  .matrix-col-header.row-label-col {
    text-align: left;
    width: 60px;
  }

  .col-head-main {
    display: block;
    color: var(--tt-slate-800);
    white-space: nowrap;
  }

  .col-head-sub {
    display: block;
    font-size: 8.5px;
    font-weight: 500;
    color: var(--tt-slate-400);
    text-transform: none;
    margin-top: 1px;
    white-space: nowrap;
  }

  .matrix-col-header.col-highlight,
  .matrix-cell.col-highlight {
    background-color: rgba(66, 133, 244, 0.03);
    border-left: 1px solid rgba(66, 133, 244, 0.15);
  }

  .matrix-cell {
    padding: 4px 3px;
    border-bottom: 1px solid var(--tt-slate-200);
    vertical-align: middle;
  }

  .matrix-cell.row-header-cell {
    width: 60px;
    text-align: left;
    padding-left: 4px;
  }

  .matrix-cell.text-right {
    text-align: right;
  }

  .matrix-cell.text-center {
    text-align: center;
  }

  .row-badge {
    display: inline-block;
    font-size: 8.5px;
    font-weight: 700;
    padding: 1px 3px;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    white-space: nowrap;
  }

  .base-badge {
    background: var(--tt-slate-100);
    color: var(--tt-slate-600);
    border: 1px solid var(--tt-slate-300);
  }

  .margin-badge {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .quote-badge {
    background: #ecfdf5;
    color: #15803d;
    border: 1px solid #a7f3d0;
  }

  .base-row {
    background: #f8fafc;
  }

  .val-text {
    font-variant-numeric: tabular-nums;
  }

  .base-val {
    color: var(--tt-slate-600);
    font-size: 10px;
    font-weight: 600;
  }

  .base-val.bold {
    font-weight: 700;
    color: var(--tt-slate-800);
  }

  /* Margin Input Cell Controls */
  .margin-input-row {
    background: #ffffff;
  }

  .margin-cell-control {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 2px;
  }

  .unit-toggle-pill {
    display: inline-flex;
    align-items: center;
    background: var(--tt-slate-100);
    border: 1px solid var(--tt-slate-300);
    border-radius: 8px;
    padding: 1px;
    cursor: pointer;
    outline: none;
    transition: all 0.12s ease;
  }

  .unit-toggle-pill:hover {
    border-color: #93c5fd;
  }

  .toggle-opt {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 12px;
    height: 13px;
    font-size: 8px;
    font-weight: 700;
    color: var(--tt-slate-500);
    border-radius: 6px;
    user-select: none;
    transition: all 0.12s ease;
  }

  .toggle-opt.active {
    background: #2563eb;
    color: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  }

  .input-with-affix {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  .matrix-number-input {
    width: 36px;
    height: 20px;
    font-size: 10px;
    font-weight: 700;
    color: var(--tt-slate-900);
    background: #ffffff;
    border: 1px solid var(--tt-slate-300);
    border-radius: 4px;
    padding: 1px 10px 1px 2px;
    text-align: right;
    font-variant-numeric: tabular-nums;
    outline: none;
    -webkit-appearance: none;
    -moz-appearance: textfield;
    appearance: textfield;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .matrix-number-input::-webkit-outer-spin-button,
  .matrix-number-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .matrix-number-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.18);
  }

  .affix-label {
    position: absolute;
    right: 2px;
    font-size: 8.5px;
    font-weight: 700;
    color: var(--tt-slate-400);
    pointer-events: none;
  }

  /* Quote Adjusted Row */
  .quote-row {
    background: rgba(22, 163, 74, 0.05);
    border-top: 1.5px solid rgba(22, 163, 74, 0.25);
  }

  .quote-row .matrix-cell {
    border-bottom: none;
    padding-top: 7px;
    padding-bottom: 7px;
  }

  .quote-val {
    color: #15803d;
    font-size: 10.5px;
    font-weight: 700;
  }

  .quote-val.bold-large {
    font-size: 11.5px;
    font-weight: 800;
    color: #0f766e;
  }

  /* Summary Footer */
  .matrix-summary-footer {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-top: 6px;
    border-top: 1px solid var(--tt-slate-100);
  }

  .summary-line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 11.5px;
    color: var(--tt-slate-600);
  }

  .summary-value.margin-added {
    color: #2563eb;
    font-weight: 700;
  }

  .summary-line.subtotal-line {
    padding-top: 4px;
    border-top: 1px solid var(--tt-slate-100);
    font-weight: 600;
    color: var(--tt-slate-800);
  }

  .summary-line.grand-total-line {
    margin-top: 4px;
    padding-top: 6px;
    border-top: 1.5px solid var(--tt-slate-200);
    align-items: flex-end;
  }

  .total-label-wrap {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .grand-label {
    font-size: 12px;
    font-weight: 800;
    color: var(--tt-slate-900);
  }

  .effective-rate-pill {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    color: var(--tt-slate-600);
  }

  .effective-rate-pill strong {
    color: #047857;
    margin-left: 3px;
  }

  .grand-total-val {
    font-size: 1.15rem;
    font-weight: 800;
    color: var(--tt-slate-900);
    font-variant-numeric: tabular-nums;
  }

  .quote-actions-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 4px;
    padding-top: 10px;
    border-top: 1px solid var(--tt-slate-200);
  }

  .secondary-action-btn {
    background: transparent;
    border: none;
    color: var(--tt-slate-500);
    font-size: 11.5px;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 6px;
    border-radius: 4px;
    transition: all 0.12s ease;
  }

  .secondary-action-btn:hover {
    color: #ef4444;
    background-color: #fee2e2;
  }

  .action-btn-group {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  :global(.action-btn-group .bx--btn) {
    border-radius: var(--tt-radius-sm);
    padding-left: 14px;
    padding-right: 14px;
    font-size: 12px;
  }

  :global(.action-btn-group .go-to-quote-action-btn.bx--btn:not(:disabled)) {
    background-color: var(--tt-primary, #2563eb) !important;
    border-color: var(--tt-primary, #2563eb) !important;
    color: #ffffff !important;
    font-weight: 700 !important;
  }

  :global(.action-btn-group .go-to-quote-action-btn.bx--btn:not(:disabled):hover) {
    background-color: #1d4ed8 !important;
    border-color: #1d4ed8 !important;
  }

  :global(.action-btn-group .go-to-quote-action-btn.bx--btn:disabled) {
    opacity: 0.55 !important;
    cursor: not-allowed !important;
    background-color: var(--tt-slate-300, #cbd5e1) !important;
    border-color: var(--tt-slate-300, #cbd5e1) !important;
    color: var(--tt-slate-500, #64748b) !important;
    pointer-events: auto !important;
  }

  :global(.action-btn-group .bx--btn:disabled),
  :global(.action-btn-group .bx--btn.bx--btn--disabled) {
    opacity: 0.55 !important;
    cursor: not-allowed !important;
    pointer-events: auto !important;
  }

  .company-search-label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2px;
  }

  .required-star {
    color: #ef4444;
    font-weight: 700;
    margin-left: 2px;
    font-size: 13px;
    line-height: 1;
  }

  .required-pill {
    display: inline-flex;
    align-items: center;
    font-size: 9.5px;
    font-weight: 700;
    color: #dc2626;
    background-color: #fee2e2;
    border: 1px solid #fecaca;
    padding: 1px 5px;
    border-radius: 4px;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    line-height: 1.2;
  }

  .company-required-hint-text {
    font-size: 10.5px;
    color: var(--tt-slate-500, #64748b);
    margin-top: 1px;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .highlight-schedule-section {
    background: linear-gradient(135deg, rgba(239, 246, 255, 0.8), rgba(240, 253, 244, 0.8)) !important;
    border: 1px solid #bfdbfe !important;
  }

  .days-display-group {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .days-val-highlight {
    color: #1d4ed8 !important;
    font-weight: 800 !important;
  }

  .single-truck-note {
    font-size: 9.5px;
    font-weight: 500;
    color: var(--tt-slate-500);
    line-height: 1.1;
  }

  .tonnes-inline-control {
    display: inline-flex;
    align-items: center;
    position: relative;
    width: 82px;
    height: 24px;
    background: #ffffff;
    border: 1px solid var(--tt-slate-300);
    border-radius: var(--tt-radius-sm);
    transition: all 0.15s ease;
    overflow: hidden;
    margin-top: 1px;
    padding: 0 4px;
  }

  .tonnes-inline-control:focus-within {
    border-color: var(--tt-primary);
    box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.2);
  }

  .tonnes-inline-input {
    width: 60px;
    height: 100%;
    border: none;
    background: transparent;
    padding: 0;
    font-size: 11.5px;
    font-weight: 700;
    color: var(--tt-slate-900);
    text-align: right;
    outline: none;
    -moz-appearance: textfield;
  }

  .tonnes-inline-input::-webkit-inner-spin-button,
  .tonnes-inline-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .tonnes-unit-label {
    font-size: 11px;
    font-weight: 600;
    color: var(--tt-slate-500);
    margin-left: 3px;
  }

  .trucks-stepper-control {
    display: inline-flex;
    align-items: center;
    position: relative;
    width: 66px;
    height: 24px;
    background: #ffffff;
    border: 1px solid var(--tt-slate-300);
    border-radius: var(--tt-radius-sm);
    transition: all 0.15s ease;
    overflow: hidden;
    margin-top: 1px;
  }

  .trucks-stepper-control:focus-within {
    border-color: var(--tt-primary);
    box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.2);
  }

  .trucks-stepper-input {
    width: 44px;
    height: 100%;
    border: none;
    background: transparent;
    padding: 0 4px;
    font-size: 11.5px;
    font-weight: 700;
    color: var(--tt-slate-900);
    text-align: center;
    outline: none;
    -moz-appearance: textfield;
  }

  .trucks-stepper-input::-webkit-inner-spin-button,
  .trucks-stepper-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .stepper-arrow-buttons {
    display: flex;
    flex-direction: column;
    width: 22px;
    height: 100%;
    border-left: 1px solid var(--tt-slate-200);
    background: var(--tt-slate-50);
  }

  .truck-step-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    color: var(--tt-slate-600);
    cursor: pointer;
    padding: 0;
    transition: all 0.12s ease;
  }

  .truck-step-btn.step-up {
    border-bottom: 1px solid var(--tt-slate-200);
  }

  .truck-step-btn:hover:not(:disabled) {
    background: var(--tt-slate-200);
    color: var(--tt-slate-900);
  }

  .truck-step-btn:active:not(:disabled) {
    background: var(--tt-slate-300);
  }

  .truck-step-btn:disabled {
    color: var(--tt-slate-300);
    cursor: not-allowed;
    background: transparent;
  }

  @media (max-width: 660px) {
    .customer-quote-window {
      width: calc(100vw - 2rem);
      right: 1rem;
    }
  }

  @media print {
    .customer-collapsed-bar,
    .customer-toggle-arrow-btn {
      display: none !important;
    }
    .collapsible-customer-fields {
      display: flex !important;
    }
  }

  /* ========================================================
     Material Tabs & Multi-Material Styles
     ======================================================== */
  .packages-count-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.15rem 0.5rem;
    background: #e8f0fe;
    color: var(--tt-primary);
    font-size: 0.7rem;
    font-weight: 700;
    border-radius: 9999px;
    border: 1px solid #bfdbfe;
    margin-left: 0.5rem;
  }

  .material-tabs-container {
    margin: 0.5rem 0 0.75rem 0;
    position: relative;
    z-index: 10;
  }

  .material-tabs-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    align-items: center;
    background: #f1f5f9;
    padding: 0.35rem 0.5rem;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
  }

  .material-tab-item {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.4rem 0.75rem;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 600;
    color: #475569;
    transition: all 0.15s ease;
    user-select: none;
  }

  .material-tab-item:hover {
    background: #e2e8f0;
    color: #1e293b;
  }

  .material-tab-item.is-active {
    background: #ffffff;
    color: #1d4ed8;
    border-color: #cbd5e1;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    font-weight: 700;
  }

  .tab-indicator-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--tt-slate-400);
    transition: background 0.15s ease;
    flex-shrink: 0;
  }

  .material-tab-item.is-active .tab-indicator-dot {
    background: #10b981;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.25);
  }

  .tab-material-title {
    font-size: 0.8rem;
    font-weight: inherit;
    color: inherit;
  }

  .tab-yard-subtitle {
    font-size: 0.7rem;
    color: var(--tt-slate-500);
    font-weight: 400;
  }

  .tab-material-price {
    display: inline-flex;
    align-items: baseline;
    gap: 0.25rem;
    padding: 0.15rem 0.45rem;
    background: #e2e8f0;
    border-radius: 4px;
    font-size: 0.775rem;
    font-weight: 700;
    color: #1e293b;
    margin-left: 0.35rem;
    white-space: nowrap;
    transition: all 0.15s ease;
  }

  .material-tab-item.is-active .tab-material-price {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .tab-price-subtext {
    font-size: 0.65rem;
    font-weight: 500;
    color: #64748b;
  }

  .material-tab-item.is-active .tab-price-subtext {
    color: #3b82f6;
  }

  .tab-close-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    margin-left: 0.2rem;
    border: none;
    background: transparent;
    border-radius: 50%;
    font-size: 0.65rem;
    color: var(--tt-slate-400);
    cursor: pointer;
    padding: 0;
    line-height: 1;
    transition: all 0.12s ease;
  }

  .tab-close-icon:hover {
    background: #fee2e2;
    color: #ef4444;
  }



  /* Combined Grand Total Summary Card */
  .combined-summary-card {
    margin-top: 1rem;
    border: 1.5px solid #bfdbfe;
    border-radius: var(--tt-radius-md);
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    overflow: hidden;
  }

  .combined-card-header {
    padding: 0.625rem 0.875rem;
    background: #eff6ff;
    border-bottom: 1px solid #dbeafe;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .combined-main-title {
    font-size: 0.825rem;
    font-weight: 700;
    color: var(--tt-slate-900);
  }

  .combined-sub-title {
    font-size: 0.7rem;
    color: var(--tt-slate-500);
    margin-left: 0.5rem;
  }

  .combined-table-wrap {
    overflow-x: auto;
  }

  .combined-materials-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.75rem;
  }

  .combined-materials-table th {
    background: var(--tt-slate-50);
    padding: 0.45rem 0.65rem;
    text-align: left;
    color: var(--tt-slate-600);
    font-weight: 600;
    border-bottom: 1px solid var(--tt-slate-200);
  }

  .combined-materials-table td {
    padding: 0.5rem 0.65rem;
    border-bottom: 1px solid var(--tt-slate-100);
    color: var(--tt-slate-800);
  }

  .combined-row {
    cursor: pointer;
    transition: background 0.12s ease;
  }

  .combined-row:hover {
    background: #f1f5f9;
  }

  .combined-row.is-active-tab-row {
    background: #e0f2fe;
    font-weight: 600;
  }

  .mat-tab-link {
    color: var(--tt-primary);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .client-rate-highlight {
    color: var(--tt-success, #059669);
    font-weight: 700;
  }

  .combined-footer-totals {
    padding: 0.75rem 0.875rem;
    background: #f8fafc;
    border-top: 1px solid var(--tt-slate-200);
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  .combined-total-row {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;
    color: var(--tt-slate-600);
  }

  .combined-total-row.subtotal {
    padding-top: 0.35rem;
    border-top: 1px dashed var(--tt-slate-200);
    font-weight: 600;
    color: var(--tt-slate-800);
  }

  .combined-grand-total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 0.5rem;
    margin-top: 0.25rem;
    border-top: 1.5px solid var(--tt-slate-300);
  }

  .combined-grand-label-group {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  /* ========================================================
     COMPANY SEARCH & UNEDITABLE COMPANY DETAILS
     ======================================================== */
  .company-search-box-wrap {
    position: relative;
    width: 100%;
    margin-bottom: 10px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .company-search-input-field {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    background: #ffffff;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 6px;
    transition: all 0.2s ease;
  }

  .company-search-input-field:focus-within {
    border-color: var(--tt-primary, #0284c7);
    box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
  }

  :global(.company-search-icon) {
    position: absolute;
    left: 10px;
    color: var(--tt-slate-400, #94a3b8);
    pointer-events: none;
  }

  .company-search-control {
    width: 100%;
    height: 34px;
    padding: 0 32px 0 34px;
    border: none;
    background: transparent;
    font-size: 13px;
    color: var(--tt-slate-800, #1e293b);
    outline: none;
  }

  .clear-input-icon-btn {
    position: absolute;
    right: 8px;
    background: transparent;
    border: none;
    color: var(--tt-slate-400, #94a3b8);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }

  .clear-input-icon-btn:hover {
    color: var(--tt-slate-600, #475569);
    background: var(--tt-slate-100, #f1f5f9);
  }

  .company-search-results-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    max-height: 240px;
    overflow-y: auto;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
    z-index: 1000;
  }

  .company-search-result-row {
    padding: 8px 12px;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .company-search-result-row:last-child {
    border-bottom: none;
  }

  .company-search-result-row:hover {
    background: #f8fafc;
  }

  .result-row-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2px;
  }

  .result-company-name {
    font-size: 12.5px;
    font-weight: 600;
    color: var(--tt-slate-800, #1e293b);
  }

  .result-company-abn {
    font-size: 11px;
    font-weight: 500;
    color: var(--tt-slate-500, #64748b);
    background: var(--tt-slate-100, #f1f5f9);
    padding: 1px 6px;
    border-radius: 4px;
  }

  .result-row-sub {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    color: var(--tt-slate-500, #64748b);
  }

  .result-company-contacts {
    color: var(--tt-primary, #0284c7);
    font-weight: 500;
  }

  .company-search-results-dropdown.empty-state {
    padding: 14px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .empty-state-text {
    font-size: 12px;
    color: var(--tt-slate-500, #64748b);
  }

  .create-company-inline-btn {
    font-size: 12px;
    font-weight: 600;
    color: var(--tt-primary, #0284c7);
    background: rgba(2, 132, 199, 0.08);
    border: 1px solid rgba(2, 132, 199, 0.2);
    border-radius: 6px;
    padding: 6px 12px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .create-company-inline-btn:hover {
    background: rgba(2, 132, 199, 0.16);
  }

  /* Selected Company Card & Uneditable Populated Details */
  .selected-company-card {
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    padding: 10px 12px;
    margin-bottom: 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .selected-company-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 6px;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
  }

  .company-header-title-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .company-header-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 6px;
    background: rgba(2, 132, 199, 0.1);
    color: var(--tt-primary, #0284c7);
  }

  .company-header-name {
    font-size: 13.5px;
    font-weight: 700;
    color: var(--tt-slate-800, #1e293b);
  }

  .company-verified-tag {
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 1px 6px;
    border-radius: 4px;
    background: #ecfdf5;
    color: #059669;
    border: 1px solid #a7f3d0;
  }

  .company-header-actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .company-edit-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    font-size: 11.5px;
    font-weight: 600;
    color: var(--tt-primary, #0284c7);
    background: rgba(2, 132, 199, 0.08);
    border: 1px solid rgba(2, 132, 199, 0.25);
    border-radius: 5px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .company-edit-btn:hover {
    background: rgba(2, 132, 199, 0.16);
  }

  .company-change-btn {
    padding: 4px 8px;
    font-size: 11.5px;
    font-weight: 500;
    color: var(--tt-slate-500, #64748b);
    background: transparent;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 5px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .company-change-btn:hover {
    background: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-700, #334155);
  }

  .company-uneditable-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .uneditable-field-item {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .uneditable-field-item.col-span-2 {
    grid-column: span 2;
  }

  .uneditable-label {
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--tt-slate-500, #64748b);
  }

  .uneditable-box {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 30px;
    padding: 0 8px;
    background: #f8fafc;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 5px;
  }

  .uneditable-value {
    font-size: 12px;
    font-weight: 500;
    color: var(--tt-slate-700, #334155);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .locked-icon {
    display: flex;
    align-items: center;
    color: var(--tt-slate-400, #94a3b8);
  }

  /* Prepared For Field & Contact Autocomplete */
  .prepared-for-field-wrap {
    position: relative;
    width: 100%;
    margin-bottom: 8px;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .prepared-for-label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .prepared-for-label-left {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .prepared-for-label-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .contacts-available-pill {
    font-size: 10.5px;
    font-weight: 500;
    color: var(--tt-slate-500, #64748b);
    background: var(--tt-slate-100, #f1f5f9);
    padding: 1px 6px;
    border-radius: 4px;
  }

  .contact-matched-badge {
    font-size: 10.5px;
    font-weight: 600;
    color: #059669;
  }

  .prepared-for-input-container {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    background: #ffffff;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 6px;
    transition: all 0.2s ease;
  }

  .prepared-for-input-container:focus-within {
    border-color: var(--tt-primary, #0284c7);
    box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
  }

  :global(.prepared-for-icon) {
    position: absolute;
    left: 10px;
    color: var(--tt-slate-400, #94a3b8);
    pointer-events: none;
  }

  .prepared-for-input {
    width: 100%;
    height: 34px;
    padding: 0 30px 0 34px;
    border: none;
    background: transparent;
    font-size: 13px;
    color: var(--tt-slate-800, #1e293b);
    outline: none;
  }

  .prepared-for-input:disabled {
    background: #f8fafc;
    cursor: not-allowed;
    color: var(--tt-slate-400, #94a3b8);
  }

  .contacts-dropdown-list {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    max-height: 220px;
    overflow-y: auto;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
    z-index: 1000;
  }

  .contacts-dropdown-header {
    padding: 6px 10px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tt-slate-400, #94a3b8);
    background: #f8fafc;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
  }

  .contact-dropdown-row {
    padding: 7px 10px;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
    cursor: pointer;
    transition: background 0.12s ease;
  }

  .contact-dropdown-row:hover {
    background: #f8fafc;
  }

  .contact-dropdown-row.active-contact {
    background: rgba(2, 132, 199, 0.08);
  }

  .contact-row-primary {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .contact-name {
    font-size: 12.5px;
    font-weight: 600;
    color: var(--tt-slate-800, #1e293b);
  }

  .contact-pos-badge {
    font-size: 10.5px;
    font-weight: 500;
    color: var(--tt-slate-600, #475569);
    background: var(--tt-slate-100, #f1f5f9);
    padding: 1px 5px;
    border-radius: 4px;
  }

  .contact-main-badge {
    font-size: 10px;
    font-weight: 600;
    color: var(--tt-primary, #0284c7);
    background: rgba(2, 132, 199, 0.1);
    padding: 1px 5px;
    border-radius: 4px;
  }

  .contact-row-details {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--tt-slate-500, #64748b);
    margin-top: 2px;
  }

  .add-new-contact-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    background: rgba(2, 132, 199, 0.05);
    border-top: 1px dashed rgba(2, 132, 199, 0.3);
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .add-new-contact-row:hover {
    background: rgba(2, 132, 199, 0.12);
  }

  .add-plus-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--tt-primary, #0284c7);
    color: #ffffff;
    font-size: 14px;
    font-weight: bold;
    flex-shrink: 0;
  }

  .add-new-contact-info {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .add-new-contact-info strong {
    font-size: 12px;
    color: var(--tt-primary, #0284c7);
  }

  .add-new-contact-info span {
    font-size: 11px;
    color: var(--tt-slate-500, #64748b);
  }

  /* Save Contact Modal Dialog */
  .save-contact-dialog {
    width: 480px;
    max-width: 95vw;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    margin: auto;
  }

  .save-contact-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 16px 18px 12px;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
    background: #f8fafc;
  }

  .save-contact-title-group {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .save-contact-icon-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: rgba(2, 132, 199, 0.12);
    color: var(--tt-primary, #0284c7);
    flex-shrink: 0;
  }

  .save-contact-heading {
    margin: 0 0 3px;
    font-size: 15px;
    font-weight: 700;
    color: var(--tt-slate-800, #1e293b);
  }

  .save-contact-subheading {
    font-size: 12px;
    color: var(--tt-slate-500, #64748b);
    line-height: 1.4;
  }

  .dialog-close-icon-btn {
    background: transparent;
    border: none;
    color: var(--tt-slate-400, #94a3b8);
    cursor: pointer;
    padding: 4px;
    border-radius: 50%;
  }

  .dialog-close-icon-btn:hover {
    color: var(--tt-slate-700, #334155);
    background: var(--tt-slate-200, #e2e8f0);
  }

  .save-contact-body {
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .contact-form-field {
    width: 100%;
  }

  .save-contact-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 18px 16px;
    border-top: 1px solid var(--tt-slate-100, #f1f5f9);
    background: #f8fafc;
  }

  .dialog-btn {
    padding: 7px 14px;
    border-radius: 6px;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .dialog-btn.secondary-btn {
    background: #ffffff;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    color: var(--tt-slate-600, #475569);
  }

  .dialog-btn.secondary-btn:hover {
    background: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-800, #1e293b);
  }

  .dialog-btn.primary-save-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: var(--tt-primary, #0284c7);
    border: 1px solid transparent;
    color: #ffffff;
  }

  .dialog-btn.primary-save-btn:hover:not(:disabled) {
    background: #0369a1;
  }

  .dialog-btn.primary-save-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Site / Delivery Location Sub-section in Material Card (Requirement 2) */
  .site-delivery-sub-section {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 8px 12px !important;
    margin-bottom: 0;
  }

  .site-delivery-sub-section .pkg-sub-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
  }

  .site-sub-title-left {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .yard-delivery-sub-section {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 8px 12px !important;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .is-best-deal .yard-delivery-sub-section {
    background: rgba(255, 255, 255, 0.85);
    border-color: #bbf7d0;
  }

  .disposal-address-input-wrap {
    width: 100%;
    margin-top: 2px;
  }

  .site-sub-badge {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 1px 5px;
    border-radius: 3px;
    background: rgba(2, 132, 199, 0.1);
    color: var(--tt-primary, #0284c7);
    margin-left: 0;
  }

  .site-delivery-input-wrap {
    width: 100%;
    margin-top: 4px;
  }

  .site-address-status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 2px 6px;
    border-radius: 4px;
    margin-left: 0;
  }

  .site-address-status.valid {
    color: #15803d;
    background: #dcfce7;
    border: 1px solid #bbf7d0;
  }

  .site-address-status.invalid {
    color: #b91c1c;
    background: #fee2e2;
    border: 1px solid #fecaca;
  }

  .site-address-alert {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.72rem;
    font-weight: 500;
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
    padding: 6px 10px;
    border-radius: 6px;
    margin-top: 6px;
    line-height: 1.35;
  }

  .site-address-alert strong {
    font-weight: 700;
  }
</style>
